import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowRight,
  BookOpen,
  Check,
  CheckCircle,
  Circle,
  Clapperboard,
  Download,
  Image,
  ImageIcon,
  LayoutGrid,
  Loader2,
  MessageSquare,
  Mic,
  PenLine,
  Play,
  Plus,
  RotateCcw,
  Camera,
  Sparkles,
  Upload,
  Users,
  Wand2,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '@/lib/formatApiError';
import { STORY_FLOWS, storyFlowNavigationState } from '@/data/storyFlows';
import {
  ATTACH_ACCEPT,
  uploadProductionAttachment,
  pollAttachmentUntilDone,
  retryAttachment,
} from '@/lib/productionAttachments';
import { isStylePlaceholderNote, styleNoteFromAnalysis } from '@/lib/visualStyleNotes';
import VoicePickerQuestion from '@/components/createPlan/VoicePickerQuestion';
import {
  CreatePlanOutputPanel,
  PUBLISH_PLATFORMS,
  downloadVideoFile,
  displayJobProgress,
  incompleteImagesMessage,
  resolvedFinalVideoUrl,
  sceneVisualStats,
  useCreationOutputs,
  videoFilename,
} from '@/components/createPlan/CreatePlanOutputPanel';
import {
  CastCharacterBuilderDialog,
  CastStatusPanel,
  CastVoiceDialog,
  autoBuildCast,
} from '@/components/createPlan/CastBuilder';
import { PlanFieldEditorDialog, PlanFieldTiles } from '@/components/createPlan/PlanFieldEditor';
import {
  CourseAlignPipelinePopup,
  CourseNarrationDialog,
  CourseStatusPanel,
  courseNarrationScript,
  courseStage,
  loadCourseState,
  pollCourseExplain,
  runCourseAlignment,
  runCourseExplain,
  runCoursePlan,
  saveCourseState,
  usesCourse,
} from '@/components/createPlan/CourseBuilder';
import {
  ReelsShortsAgentPanel,
  ReelsShortsPlacementDialog,
  ReelsShortsTranscriptDialog,
} from '@/components/createPlan/ReelsShortsAgentPanel';
import { UnifiedStoryPipelineDialog } from '@/components/createPlan/UnifiedStoryPipelineDialog';
import { StoryChatQuestionnaire } from '@/components/createPlan/StoryChatQuestionnaire';
import { StoryFormatReview } from '@/components/createPlan/StoryFormatReview';
import { screenplayVoiceRows, StructuredScreenplayReview } from '@/components/createPlan/StructuredScreenplayReview';
import { cleanAllToast } from '@/lib/reelsShortsAudioClean';
import { openScriptAudioAlignInEditor } from '@/lib/scriptAudioAlignCourse';
import api from '@/lib/api';
import { reelsShortsEditorPath } from '@/lib/reelsShortsEditor';
import { YouTubePublishDialog } from '@/components/YouTubePublishDialog';
import { InstagramPublishDialog } from '@/components/InstagramPublishDialog';
import {
  buildScriptBasedNextSteps,
  scriptBasedImagesInProgress,
  scriptBasedPromptsJobStuck,
  scriptBasedReadyForScenes,
} from '@/lib/scriptBasedPipeline';
import {
  projectEditorPath,
  answerCreationPlan,
  assignCastVoices,
  castStage,
  addCastCharacter,
  detectCastCharacters,
  fetchStoryContext,
  lockStoryCharacters,
  usesCast,
  writeCastOverview,
  createCreationPlan,
  executeCreationPlan,
  fetchCreationPlan,
  fetchJob,
  updateStoryFormat,
  writeStructuredScreenplay,
  editStructuredScreenplay,
  approveStructuredScreenplay,
  buildMultiAnswer,
  canAnswerOther,
  canRetryExecution,
  canRestartDialogueStory,
  hasCreationOutputs,
  rerenderCreationPlan,
  exportFinalCreationPlan,
  convertCreationPlanImagesToVideos,
  retryCreationPlan,
  fieldLabel,
  formatFieldValue,
  generateCreationAudio,
  generateCreationScript,
  initialChoicePicks,
  isMultiChoice,
  optionsHaveImages,
  readField,
  splitInlineOptions,
  saveCreationTranscript,
  sfxStepLabel,
  CREATE_AGENT_PARAM,
  withCreateAgentParams,
} from '@/lib/creationPlan';

import { REFERENCE_FRAMES } from '@/lib/unifiedStoryPipeline';
import { TwinneeLogo } from '@/components/TwinneeLogo';
const INLINE_IMAGE_OPTIONS = 5;
const INLINE_TEXT_OPTIONS = 8;

/** When true, pipeline step buttons (Generate prompts / Generate images) are
 *  shown inline inside the create-agent chat progress card instead of only in
 *  the separate Task History pipeline dialog. Controlled by env var so it can
 *  be toggled without a code deploy. */
const PIPELINE_STEPS_IN_CHAT = process.env.REACT_APP_PIPELINE_STEPS_IN_CHAT === 'true';

function OptionThumb({ opt, className = '' }) {
  const [failed, setFailed] = useState(false);
  if (!opt.image || failed) {
    return (
      <span className={`${className} shrink-0 bg-gradient-to-br from-[#2DD4BF]/30 to-[#7DD3FC]/20 flex items-center justify-center`}>
        <TwinneeLogo className="w-3 h-3 text-[#2DD4BF]" />
      </span>
    );
  }
  return (
    <img
      src={opt.image}
      alt=""
      loading="lazy"
      onError={() => setFailed(true)}
      className={`${className} shrink-0 object-cover bg-white/5`}
    />
  );
}

const PREVIEW_FIELDS = [
  'title',
  'script',
  'language',
  'genre',
  'tone',
  'audience',
  'flow_params.story_format',
  'flow_params.include_sfx',
  'length',
  'duration_seconds',
  'scene_count',
  'aspect_ratio',
  'visual.style',
  'visual.image_provider',
  'visual.video_provider',
  'voice.name',
  'voice.provider',
  'voice.style',
  'voice.voice_id',
  'options.subtitles',
  'options.music',
  'flow_params.recording_mode',
  'flow_params.characters',
  'flow_params.anchor_name',
  'flow_params.screen_share',
  'flow_params.speaker_camera',
  'flow_params.camera',
  'flow_params.editing_mode',
  'flow_params.cleanup_level',
  'flow_params.use_avatar',
  'flow_params.insert_types',
  'flow_params.avatar_name',
  'flow_params.heygen_voice_id',
  'assets.audio',
  'assets.video',
  'assets.document',
  'assets.image',
];

const TERMINAL_JOB_STATES = ['completed', 'failed', 'cancelled'];
/** Pipeline paused for user action (transcripts / AI editor) — not a hang. */
const REVIEW_JOB_STATES = ['awaiting_review'];

function usesReels(planOrExec) {
  const id = planOrExec?.flow?.id || planOrExec?.flow_id || '';
  return id === 'reels_shorts';
}

function jobFailureReason(job) {
  return String(job?.error_message || job?.error || job?.message || job?.status || 'unknown error').trim();
}

const GREETING =
  "What would you like to create? Describe the video — topic, style, length, language — and I'll pick the right flow, draft a plan, and ask for anything missing.";

const OVERVIEW_EXCERPT_CHARS = 420;

const CAST_BUSY_LABELS = {
  overview: 'Writing the story overview… (up to a minute)',
  detect: 'Finding the characters in your story…',
  'auto-build': 'Building the characters — drawing their portraits…',
  'auto-voices': 'Allocating a voice to each character…',
};

function excerpt(text, max = OVERVIEW_EXCERPT_CHARS) {
  const s = String(text || '').trim();
  return s.length > max ? `${s.slice(0, max).replace(/\s+\S*$/, '')}…` : s;
}

/** What the user does next in a dialogue plan's cast step. */
function castNextHint(plan) {
  switch (castStage(plan)) {
    case 'overview':
      return "Hit **Write story overview** and I'll draft the story, then find its characters.";
    case 'characters':
      return plan?.cast?.characters?.length
        ? 'The story overview is ready. Open **Build characters** to shape each look and personality, or let me build them.'
        : "The story overview is ready. Hit **Find characters** and I'll detect who's in it.";
    case 'voices':
      return 'The characters are built. Open **Allocate voices** to give each one a voice, or let me assign them.';
    case 'done':
      return 'The cast is ready. Hit **Generate story** to create the scenes.';
    default:
      return '';
  }
}

function voiceSummary(plan) {
  const lines = (plan?.cast?.characters || []).map((m) => `${m.name} — ${m.voice?.name || m.voice?.voice_id || 'no voice'}`);
  const narrator = plan?.cast?.narrator?.voice;
  if (plan?.cast_status?.narrator_needed && narrator?.voice_id) lines.push(`Narrator — ${narrator.name || narrator.voice_id}`);
  return lines.join('\n');
}

function newId(prefix) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function stripMd(s) {
  return String(s || '').replace(/\*\*(.+?)\*\*/g, '$1');
}

/**
 * Dashboard "create anything" agent: detects what the user wants, lets GPT draft a
 * uniform JSON plan for the chosen flow, asks the pending fields one by one, then
 * executes the flow (or opens its page prefilled). Pass `resumePlanId` to reopen a saved
 * conversation; with neither a seed nor a plan id it starts blank and asks what to create.
 * With `syncUrl`, a started generation is reflected in the page URL as `?agent=<plan>&story=<project>`
 * (the host page reopens the studio from `agent`); closing the studio removes both params.
 */
/** A handoff to a blank create-agent chat did not start a story. */
function blankAgentHandoff(exec) {
  if (!exec || exec.mode !== 'handoff' || exec.project_id || exec.job_id) return false;
  const route = String(exec.route || exec.handoff?.route || '');
  return /(?:\?|&)agent=new(?:&|$)/.test(route);
}

export function CreatePlanChatStudio({
  open,
  onClose,
  navigate,
  seed,
  resumePlanId = null,
  onTaskCreated,
  onNewPlan = null,
  syncUrl = false,
}) {
  const [plan, setPlan] = useState(null);
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [composer, setComposer] = useState('');
  const [uploadBusy, setUploadBusy] = useState(false);
  const [execution, setExecution] = useState(null);
  const [job, setJob] = useState(null);
  const [previewTab, setPreviewTab] = useState('plan');
  const [mobilePane, setMobilePane] = useState('chat');
  const overlayRef = useRef(null);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const lastAskedRef = useRef('');
  const readyAnnouncedRef = useRef(false);
  const scriptLangAnnouncedRef = useRef('');
  const pollRef = useRef(null);
  const sessionRef = useRef(0);
  const sessionPlanIdRef = useRef(null);
  const persistRef = useRef({ planId: null, messages: [], inFlight: false, dirty: false });
  const seedRef = useRef(seed);
  seedRef.current = seed;

  // ── Style-reference image uploads (for visual_style question) ────────────
  // Declared here so all effects below can reference them without TDZ errors
  const [styleImages, setStyleImages] = useState([]);
  const [styleUploading, setStyleUploading] = useState(false);
  const [detectedStyleNote, setDetectedStyleNote] = useState(() => {
    const seedNote = (seed?.imageStyleNotes || '').trim();
    return isStylePlaceholderNote(seedNote) ? '' : seedNote;
  });
  const [stylePopupOpen, setStylePopupOpen] = useState(false);
  const [styleNoteEditing, setStyleNoteEditing] = useState('');
  const styleImgInputRef = useRef(null);
  const styleProjectIdRef = useRef(null);

  // ── Seed attachment display + lightbox ────────────────────────────────────
  const seedAttachments = seed?.seedAttachments || [];
  const [lightboxUrl, setLightboxUrl] = useState(null); // null = closed

  // Sync detectedStyleNote from seed / plan input so the banner stays visible
  // after the visual.style question is answered and after execute.
  // Skip while ignoreSeedStyleRef is set (New plan) so old seed notes cannot rehydrate.
  // Ignore upload placeholders like "Image reference saved (…)" — those are not style prose.
  const ignoreSeedStyleRef = useRef(false);
  useEffect(() => {
    if (ignoreSeedStyleRef.current) return;
    const incoming = (
      seed?.imageStyleNotes
      || plan?.input?.visual?.style_notes
      || plan?.input?.image_style_notes
      || ''
    ).trim();
    if (!incoming || isStylePlaceholderNote(incoming)) return;
    setDetectedStyleNote(incoming);
  }, [seed?.imageStyleNotes, plan?.input?.visual?.style_notes, plan?.input?.image_style_notes]);

  const question = plan?.next_question || null;
  const executed = Boolean(execution) && !blankAgentHandoff(execution);
  const [choicePicks, setChoicePicks] = useState([]);
  const [otherOpen, setOtherOpen] = useState(false);
  const [otherText, setOtherText] = useState('');

  // Auto-analyze seed image attachments when visual.style question appears and no style yet
  const autoStyleAnalyzedRef = useRef(false);
  useEffect(() => {
    const isStyleQ = question?.field === 'visual.style' || question?.field === 'visual_style';
    const imgIds = seed?.imageAttachmentIds || [];
    if (!isStyleQ || detectedStyleNote || !imgIds.length || autoStyleAnalyzedRef.current) return;
    autoStyleAnalyzedRef.current = true;
    setStyleUploading(true);
    (async () => {
      try {
        const results = await Promise.all(
          imgIds.map(async (id) => {
            try {
              // Trigger a fresh Gemini analysis via retry
              await retryAttachment(id);
              const done = await pollAttachmentUntilDone(id, { intervalMs: 1800, timeoutMs: 60000 });
              const desc = styleNoteFromAnalysis(done?.analysis);
              return { id, desc, previewUrl: done?.storage_url || '', status: 'done' };
            } catch {
              return { id, desc: '', previewUrl: '', status: 'done' };
            }
          })
        );
        const combined = results.map((r) => r.desc).filter(Boolean).join(' ').slice(0, 800);
        if (combined) {
          setDetectedStyleNote(combined);
          setStyleImages(results.map((r) => ({ ...r, styleDesc: r.desc })));
        }
      } finally {
        setStyleUploading(false);
      }
    })();
  }, [question?.field, seed?.imageAttachmentIds, detectedStyleNote]);
  const [optionsPopupOpen, setOptionsPopupOpen] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [retryAction, setRetryAction] = useState('');
  const [rerendering, setRerendering] = useState(false);
  const [retryingExport, setRetryingExport] = useState(false);
  const [convertingImages, setConvertingImages] = useState(false);
  const convertAskRef = useRef('');
  const [songFormOpen, setSongFormOpen] = useState(false);
  const [songPrompt, setSongPrompt] = useState('');
  const [songLyrics, setSongLyrics] = useState('');
  const [songLyricsOpen, setSongLyricsOpen] = useState(false);
  const [songInstrumental, setSongInstrumental] = useState(false);
  const [scriptFormOpen, setScriptFormOpen] = useState(false);
  const [scriptPrompt, setScriptPrompt] = useState('');
  const [formatSampleExpanded, setFormatSampleExpanded] = useState('');
  const songGeneration = plan?.audio_generation || null;
  const songRunning = songGeneration?.status === 'running';
  const questionKey = question ? `${plan?.id}:${question.id}` : '';
  const [publishDialog, setPublishDialog] = useState(null);
  const [publish, setPublish] = useState({});
  const [castBusy, setCastBusy] = useState('');
  const [castDialog, setCastDialog] = useState(null);
  const [course, setCourse] = useState(null);
  const [courseBusy, setCourseBusy] = useState('');
  const [courseDialog, setCourseDialog] = useState(null);
  const [alignPopupOpen, setAlignPopupOpen] = useState(false);
  const [storyPipelineOpen, setStoryPipelineOpen] = useState(false);
  const unifiedStory = plan?.flow?.id === 'unified_story';
  const alignStartedRef = useRef('');
  const [reelsBusy, setReelsBusy] = useState('');
  const [reelsDialog, setReelsDialog] = useState(null);
  const [reelsRefreshKey, setReelsRefreshKey] = useState(0);
  const reelsReviewAnnouncedRef = useRef('');
  const reelsPolishStartedRef = useRef('');
  const reelsAfterRetranscribeRef = useRef(false);
  const runReelsAudioPolishRef = useRef(null);

  // --- Script-based pipeline inline steps (PIPELINE_STEPS_IN_CHAT flag) ---
  const [sbProject, setSbProject] = useState(null);
  const [pipelinePhaseLoading, setPipelinePhaseLoading] = useState(null);
  const castPlan = usesCast(plan);
  const coursePlanFlow = usesCourse(plan);
  const reelsPlan = usesReels(plan) || usesReels(execution);
  const currentCastStage = castStage(plan);
  const currentCourseStage = courseStage(course);
  const {
    outputs,
    loading: outputsLoading,
    error: outputsError,
    refresh: refreshOutputs,
  } = useCreationOutputs(open ? plan?.id : null, execution, job?.status);
  const showOutputs = hasCreationOutputs(execution);
  const finalVideoUrl = resolvedFinalVideoUrl(outputs, job);
  const autoPreviewedRef = useRef(false);

  useEffect(() => {
    if (!open) {
      setMobilePane('chat');
      autoPreviewedRef.current = false;
      return undefined;
    }
    if (showOutputs && !autoPreviewedRef.current) {
      autoPreviewedRef.current = true;
      setMobilePane('preview');
    }
    return undefined;
  }, [open, showOutputs]);

  // --- Script-based pipeline inline steps ---
  const isScriptBasedExecution = (plan?.flow?.id || execution?.flow_id) === 'script_based';
  const sbProjectId = isScriptBasedExecution
    ? (execution?.project_id || outputs?.narration_project_id || null)
    : null;
  const sbSegments = sbProject?.segments || [];
  const sbVisualStyle = sbProject?.input?.visual_style || '';
  const sbIsWebScraper = sbVisualStyle === 'web_scraper';
  const sbNextSteps = (PIPELINE_STEPS_IN_CHAT && sbProjectId && sbSegments.length > 0)
    ? buildScriptBasedNextSteps(sbSegments, sbVisualStyle)
    : [];
  const sbReadyForScenes = scriptBasedReadyForScenes(sbSegments, sbVisualStyle);
  const sbImagesInProgress = scriptBasedImagesInProgress(sbSegments, job);
  const sbJobRunning = !!(job && !TERMINAL_JOB_STATES.includes(job?.status));
  const sbPromptsStuck = scriptBasedPromptsJobStuck(job, sbSegments);

  useEffect(() => {
    if (!PIPELINE_STEPS_IN_CHAT || !sbProjectId || !open) {
      setSbProject(null);
      return undefined;
    }
    let cancelled = false;
    let intervalId = null;
    const stopPolling = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };
    const load = async () => {
      try {
        // Prefer live segments so the checklist updates without a full page refresh.
        const [{ data: project }, { data: segsPayload }] = await Promise.all([
          api.get(`/narration/projects/${sbProjectId}`),
          api.get(`/narration/projects/${sbProjectId}/segments`).catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        const liveSegs = Array.isArray(segsPayload)
          ? segsPayload
          : (segsPayload?.segments || segsPayload?.items || null);
        const next = liveSegs?.length
          ? { ...project, segments: liveSegs }
          : project;
        setSbProject(next);
        const stepsDone = scriptBasedReadyForScenes(
          next?.segments || [],
          next?.input?.visual_style || '',
        );
        const jobSettled = !job
          || TERMINAL_JOB_STATES.includes(job.status)
          || Number(job.progress) >= 100;
        // Keep polling while scenes are still filling in — orphan jobs often sit on
        // status=completed with progress < 100 until poll-repair finalizes them.
        if (stepsDone && jobSettled && Number(job?.progress || 0) >= 100) {
          stopPolling();
          refreshOutputs();
        }
      } catch { /* transient — ignore */ }
    };
    load();
    intervalId = setInterval(load, 2500);
    return () => {
      cancelled = true;
      stopPolling();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sbProjectId, job?.status, job?.progress, job?.message]);

  const runSbPhase = async (phase, request) => {
    if (!sbProjectId) return;
    setPipelinePhaseLoading(phase);
    try {
      const { data } = await request();
      const nextJobId = data.job_id || job?.id;
      setJob((prev) => ({
        ...(prev || {}),
        id: nextJobId,
        status: 'running',
        message: phase === 'prompts' ? 'Generating image prompts…' : 'Generating images…',
      }));
      startJobPolling(nextJobId);
      toast.message(phase === 'prompts' ? 'Generating image prompts…' : 'Fetching images…');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start next step');
    } finally {
      setPipelinePhaseLoading(null);
    }
  };

  const sbGeneratePrompts = () => runSbPhase(
    'prompts',
    () => api.post(`/narration/script-based/projects/${sbProjectId}/generate-image-prompts`),
  );
  const sbGenerateImages = () => runSbPhase(
    'images',
    () => api.post(`/narration/script-based/projects/${sbProjectId}/generate-images`),
  );

  useEffect(() => {
    if (execution?.mode === 'api' && execution.job_id) setPreviewTab('output');
  }, [execution?.mode, execution?.job_id]);

  useEffect(() => {
    setPublish({});
    setPublishDialog(null);
    setCastDialog(null);
    setCastBusy('');
    setCourseDialog(null);
    setCourseBusy('');
    setReelsDialog(null);
    setReelsBusy('');
    setReelsRefreshKey(0);
    reelsReviewAnnouncedRef.current = '';
    setCourse(plan?.id ? loadCourseState(plan.id) : null);
  }, [plan?.id]);

  useEffect(() => {
    if (plan?.id && course) saveCourseState(plan.id, course);
  }, [plan?.id, course]);

  useEffect(() => {
    const jobId = course?.explain_job_id;
    if (!open || !jobId) return undefined;
    if (course.explain_status === 'completed' || course.explain_status === 'failed') return undefined;
    let cancelled = false;
    let timer;
    const tick = async () => {
      try {
        const data = await pollCourseExplain(jobId);
        if (cancelled) return;
        setCourse((prev) => {
          if (!prev || prev.explain_job_id !== jobId) return prev;
          return {
            ...prev,
            explain_job: data,
            explain_status: data.status || prev.explain_status,
            stage: data.status === 'completed' ? 'align' : prev.stage,
          };
        });
        if (data.status === 'completed') {
          pushAssistant('Lesson artifacts are ready. Starting the alignment pipeline now — progress is in the popup and on the right.');
          return;
        }
        if (data.status === 'failed') {
          pushAssistant(`Artifact generation failed: ${data.error_message || 'unknown error'}`);
          return;
        }
      } catch {
        /* transient */
      }
      if (!cancelled) timer = setTimeout(tick, 2500);
    };
    tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, course?.explain_job_id, course?.explain_status]);

  const startCourseAlignment = async (sourceCourse = course, sourcePlan = plan) => {
    if (!sourcePlan?.id || courseBusy === 'align') return;
    const active = sourceCourse?.explain_job ? sourceCourse : resolveCourseState();
    if (active?.align_status === 'completed' && active?.align_result) {
      setAlignPopupOpen(true);
      setPreviewTab('plan');
      return;
    }
    if (active?.explain_status !== 'completed' || !active?.explain_job) {
      toast.error('Wait for lesson artifacts to finish first');
      return;
    }
    const startKey = `${sourcePlan.id}:${active.explain_job_id}`;
    if (alignStartedRef.current === startKey && active.align_status === 'running') return;
    alignStartedRef.current = startKey;
    setCourseBusy('align');
    setAlignPopupOpen(true);
    setPreviewTab('plan');
    setCourse((prev) => ({
      ...(prev || active),
      ...active,
      align_status: 'running',
      align_error: null,
      stage: 'align',
    }));
    pushUser('Run alignment pipeline');
    pushAssistant('Running script → audio alignment and placing lesson visuals — status is in the popup and on the right.');
    try {
      const next = await runCourseAlignment(active, sourcePlan, {
        onProgress: (partial) => {
          setCourse((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              align_status: partial?.status === 'completed' ? 'completed' : 'running',
              align_result: { ...(prev.align_result || {}), ...partial },
              align_story_id: partial?.story_id || prev.align_story_id,
              align_job_id: partial?.job_id || prev.align_job_id,
            };
          });
        },
      });
      setCourse(next);
      pushAssistant(
        'Alignment finished. Preview and pipeline details are on the right — open the editor when you want to refine slides.'
      );
      toast.success('Alignment complete');
    } catch (err) {
      const msg = formatApiError(err, 'Alignment failed');
      toast.error(msg);
      pushAssistant(`Alignment failed: ${msg}`);
      setCourse((prev) => ({
        ...(prev || active),
        align_status: 'failed',
        align_error: msg,
        stage: 'align',
      }));
      alignStartedRef.current = '';
    } finally {
      setCourseBusy('');
    }
  };

  // Auto-run alignment once lesson artifacts complete.
  useEffect(() => {
    if (!open || !plan?.id || !course) return;
    if (course.explain_status !== 'completed' || !course.explain_job) return;
    if (course.align_status === 'completed' || course.align_status === 'running') return;
    if (courseBusy) return;
    const key = `${plan.id}:${course.explain_job_id}`;
    if (alignStartedRef.current === key) return;
    startCourseAlignment(course, plan);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, plan?.id, course?.explain_status, course?.explain_job_id, course?.explain_job, course?.align_status, courseBusy]);

  const openPublish = (platform) => {
    if (!finalVideoUrl) {
      toast.error('The video is not ready yet');
      return;
    }
    setPreviewTab('output');
    setPublishDialog(platform);
  };

  const publishCallbacks = (platform) => ({
    onPublishStart: () => setPublish((p) => ({ ...p, [platform]: { status: 'publishing' } })),
    onPublished: ({ url }) => {
      setPublish((p) => ({ ...p, [platform]: { status: 'published', url } }));
      const label = platform === 'youtube' ? 'YouTube' : 'Instagram';
      pushAssistant(url ? `Posted to ${label}: ${url}` : `Posted to ${label}.`);
      refreshOutputs();
    },
    onPublishFailed: (message) => setPublish((p) => ({ ...p, [platform]: { status: 'failed', error: message } })),
  });

  const closePublishDialog = (nextOpen) => {
    if (nextOpen) return;
    setPublishDialog(null);
    // The dialog may have connected an account through the OAuth popup.
    refreshOutputs();
  };

  useEffect(() => {
    setChoicePicks(initialChoicePicks(question));
    setOtherOpen(false);
    setOtherText('');
    setOptionsPopupOpen(false);
    setSongFormOpen(false);
    setSongPrompt(question?.ai_generate?.default_prompt || '');
    setSongLyrics(question?.ai_generate?.default_lyrics || '');
    setSongLyricsOpen(Boolean(question?.ai_generate?.default_lyrics));
    setSongInstrumental(false);
    setScriptFormOpen(false);
    setScriptPrompt('');
    setFormatSampleExpanded('');
    // Reset only when a different question is shown, not on every plan object refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionKey]);

  const pushAssistant = useCallback((content, extra = {}) => {
    setMessages((prev) => [...prev, { id: newId('a'), role: 'assistant', content, ...extra }]);
  }, []);

  const pushUser = useCallback((content) => {
    setMessages((prev) => [...prev, { id: newId('u'), role: 'user', content }]);
  }, []);

  useEffect(() => {
    if (!open || !plan?.id || job?.status !== 'completed') return;
    if (!outputs?.can_convert_images_to_videos) return;
    const pending = Number(outputs.pending_ai_videos) || 0;
    if (pending <= 0) return;
    const key = `${plan.id}:${pending}`;
    if (convertAskRef.current === key) return;
    convertAskRef.current = key;
    pushAssistant(
      `This cut is still image-based (${pending} scene${pending === 1 ? '' : 's'} without AI video). `
      + 'Want me to convert all images to AI videos with multi-speaker focus? '
      + 'Use **Convert all images to videos** on the right, or say the word here.',
    );
  }, [
    open,
    plan?.id,
    job?.status,
    outputs?.can_convert_images_to_videos,
    outputs?.pending_ai_videos,
    pushAssistant,
  ]);

  const announce = useCallback(
    (next, { force = false, autoStart = false } = {}) => {
      setPlan(next);
      const det = next?.script_language;
      const preferred = String(det?.preferred || '').trim();
      if (preferred && scriptLangAnnouncedRef.current !== preferred) {
        scriptLangAnnouncedRef.current = preferred;
        const names = {
          en: 'English', hi: 'Hindi', es: 'Spanish', fr: 'French', de: 'German',
          ta: 'Tamil', te: 'Telugu', bn: 'Bengali', mr: 'Marathi',
        };
        const label = names[preferred] || preferred;
        const mixed = Array.isArray(det?.languages) && det.languages.length > 1;
        pushAssistant(
          mixed
            ? `I detected a mixed-language script — prioritizing **${label}** for narration voices (English last). Suggestions will favor voices that speak ${label} clearly.`
            : `I detected **${label}** in your script — I'll suggest narrator voices that speak ${label} for clearer pronunciation.`
        );
      }
      const q = next?.next_question;
      const storyState = next?.story_workflow?.state;
      const screenplayWaits = q?.field === 'structured_screenplay' && (
        storyState === 'format_review' || storyState === 'format_locked' || storyState === 'character_review'
      );
      if (q && !screenplayWaits) {
        readyAnnouncedRef.current = false;
        if (force || lastAskedRef.current !== q.field) {
          lastAskedRef.current = q.field;
          const remaining = next.questions?.length || 1;
          const tag = q.required ? '' : ' (optional)';
          pushAssistant(`${q.prompt}${tag}${remaining > 1 ? `\n\n(${remaining} questions left)` : ''}`);
        }
        return;
      }
      if (screenplayWaits) return;
      lastAskedRef.current = '';
      if (
        next?.flow?.id === 'unified_story' &&
        usesCast(next) &&
        castStage(next) &&
        castStage(next) !== 'done' &&
        !readyAnnouncedRef.current
      ) {
        readyAnnouncedRef.current = true;
        pushAssistant(
          'Character views and voices are set in the character builder. Generate an AI portrait or upload a photo for each character, then allocate a voice.'
        );
        return;
      }
      if (next?.ready && !readyAnnouncedRef.current) {
        readyAnnouncedRef.current = true;
        if (usesCast(next) && castStage(next) !== 'done') {
          pushAssistant(
            autoStart && castStage(next) === 'overview'
              ? "All details collected. First I'll write the story overview, then we'll build the characters and allocate a voice to each one."
              : castNextHint(next)
          );
        } else if (usesCourse(next)) {
          pushAssistant(
            autoStart
              ? "All details collected. I'll plan the curriculum and generate lesson artifacts next — progress stays on the right."
              : 'All details collected. Hit **Analyze & plan course** — I\'ll build the curriculum and generate lesson artifacts. Status stays on the right.'
          );
        } else if (autoStart) {
          pushAssistant(
            next.flow?.execution === 'api'
              ? `All details collected. Starting the ${next.flow.label} pipeline now…`
              : `All details collected. Opening ${next.flow.label} with everything filled in…`
          );
        } else {
          pushAssistant(
            next.flow?.execution === 'api'
              ? `The plan is complete. Hit **Execute plan** and I'll start the ${next.flow.label} pipeline.`
              : `The plan is complete. Hit **Execute plan** to open ${next.flow.label} with everything filled in.`
          );
        }
      }
    },
    [pushAssistant]
  );

  const syncUrlParams = useCallback(
    (updates) => {
      if (!syncUrl || typeof window === 'undefined' || !navigate) return;
      const { pathname, search, hash } = window.location;
      const next = withCreateAgentParams(search, updates);
      if (next === search) return;
      navigate(`${pathname}${next}${hash}`, { replace: true });
    },
    [navigate, syncUrl]
  );

  const closeStudio = () => {
    syncUrlParams({ planId: null, storyId: null });
    onClose?.();
  };

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const flushTranscript = useCallback(async () => {
    const st = persistRef.current;
    if (st.inFlight) {
      st.dirty = true;
      return;
    }
    st.inFlight = true;
    try {
      do {
        st.dirty = false;
        const { planId, messages: snapshot } = st;
        if (planId && snapshot.length) {
          // eslint-disable-next-line no-await-in-loop
          await saveCreationTranscript(planId, snapshot).catch(() => {});
        }
      } while (st.dirty);
    } finally {
      st.inFlight = false;
    }
  }, []);

  useEffect(() => {
    if (!open || !plan?.id || restoring || !messages.length) return;
    persistRef.current.planId = plan.id;
    persistRef.current.messages = messages;
    flushTranscript();
  }, [open, plan?.id, messages, restoring, flushTranscript]);

  const clearDetectedVisualStyle = () => {
    setDetectedStyleNote('');
    setStyleImages([]);
    setStyleNoteEditing('');
    setStylePopupOpen(false);
    setStyleUploading(false);
    autoStyleAnalyzedRef.current = false;
    styleProjectIdRef.current = null;
  };

  const resetSession = () => {
    sessionRef.current += 1;
    sessionPlanIdRef.current = null;
    stopPolling();
    setPlan(null);
    setMessages([]);
    setComposer('');
    setExecution(null);
    setJob(null);
    setPreviewTab('plan');
    setBusy(false);
    setRestoring(false);
    lastAskedRef.current = '';
    readyAnnouncedRef.current = false;
    scriptLangAnnouncedRef.current = '';
    clearDetectedVisualStyle();
  };

  const startPlan = async (s) => {
    const session = sessionRef.current;
    const text = String(s.text || '').trim();
    if (text) pushUser(text);
    setBusy(true);
    try {
      const created = await createCreationPlan({
        text,
        transcript: s.transcript || '',
        extractedText: s.extractedText || '',
        attachmentIds: s.attachmentIds || [],
        productionProjectId: s.projectId || null,
        preferredFlowId: s.preferredFlowId || '',
        seedInput: s.params && typeof s.params === 'object' ? s.params : null,
      });
      if (session !== sessionRef.current) return;
      sessionPlanIdRef.current = created.id;
      const requiredCount = created.questions?.filter((q) => q.required).length || 0;
      const optionalCount = (created.questions?.length || 0) - requiredCount;
      const parts = [];
      if (requiredCount) parts.push(`${requiredCount} required`);
      if (optionalCount) parts.push(`${optionalCount} optional (skip any to use the default)`);
      let intro;
      if (created.format_unclear) {
        const fallback = created.story_format_pending
          ? "This sounds like a story, but you didn't say how it should be told"
          : 'This could be made in a few different ways';
        const askLanguageFirst = created.next_question?.field === 'language';
        intro = askLanguageFirst
          ? `${String(created.rationale || fallback).replace(/[.\s]+$/, '')} — I'll ask the language first so the sample scenes match, then how the story should be told.`
          : `${String(created.rationale || fallback).replace(/[.\s]+$/, '')} — so I'll ask that first.`;
      } else if (created.flow_pending) {
        intro = `This looks like **${created.flow.label}**, but I'm not sure — please confirm the kind of video first.`;
      } else {
        intro = `I'll make this as **${created.flow.label}**.` + (created.rationale ? ` ${created.rationale}` : '');
      }
      pushAssistant(
        intro +
          (parts.length
            ? `\n\nI have a few questions: ${parts.join(' and ')}. Once they're answered I'll start the video.`
            : '')
      );
      announce(created);
    } catch (err) {
      if (session === sessionRef.current) {
        pushAssistant(`I couldn't build a plan: ${formatApiError(err, 'planner unavailable')}`);
      }
    } finally {
      if (session === sessionRef.current) setBusy(false);
    }
  };

  const resumePlan = async (planId) => {
    const session = sessionRef.current;
    sessionPlanIdRef.current = planId;
    setBusy(true);
    setRestoring(true);
    try {
      const { plan: loaded, transcript } = await fetchCreationPlan(planId);
      if (session !== sessionRef.current) return;
      const loadedExec = loaded.status === 'executed' && loaded.execution ? loaded.execution : null;
      const exec = blankAgentHandoff(loadedExec) ? null : loadedExec;
      setExecution(exec);
      if (transcript.length) {
        lastAskedRef.current = loaded.next_question?.field || '';
        readyAnnouncedRef.current = Boolean(loaded.ready || exec);
        setPlan(loaded);
        setMessages(
          transcript.map((m) => ({ id: m.id || newId(m.role === 'user' ? 'u' : 'a'), role: m.role, content: m.content }))
        );
      } else {
        const title = loaded.input?.title || loaded.suggestions?.title;
        pushAssistant(
          `Picking up your **${loaded.flow.label}** plan${title ? ` “${title}”` : ''}.` +
            (exec
              ? exec.mode === 'handoff'
                ? ` It was opened in ${loaded.flow.label}.`
                : ' It has already started — progress is below.'
              : '')
        );
        if (exec) {
          readyAnnouncedRef.current = true;
          setPlan(loaded);
        } else {
          announce(loaded, { force: true });
        }
      }
      if (exec?.mode === 'api' && exec.project_id) syncUrlParams({ planId: loaded.id, storyId: exec.project_id });
      if (exec?.job_id) startJobPolling(exec.job_id, { quietIfDone: true });
    } catch (err) {
      if (session === sessionRef.current) {
        pushAssistant(`I couldn't reopen that conversation: ${formatApiError(err, 'plan not found')}`);
      }
    } finally {
      if (session === sessionRef.current) {
        setBusy(false);
        setRestoring(false);
      }
    }
  };

  const startBlank = () => {
    // Block seed/plan style sync from re-applying the previous plan's detected style.
    ignoreSeedStyleRef.current = true;
    resetSession();
    try {
      onNewPlan?.();
    } catch {
      /* parent clear is best-effort */
    }
    if (typeof window !== 'undefined' && new URLSearchParams(window.location.search).has(CREATE_AGENT_PARAM)) {
      syncUrlParams({ planId: 'new', storyId: null });
    }
    pushAssistant(GREETING);
  };

  useEffect(() => {
    if (!open) {
      sessionRef.current += 1;
      stopPolling();
      return undefined;
    }
    // Opening (or reopening) with a fresh seed / resume may carry style notes.
    ignoreSeedStyleRef.current = false;
    resetSession();
    const s = seedRef.current || {};
    // Re-apply seed style after reset (sync effect may not re-fire if seed notes unchanged).
    const seedStyle = String(s.imageStyleNotes || '').trim();
    if (seedStyle) setDetectedStyleNote(seedStyle);
    const hasSeed = Boolean(
      String(s.text || '').trim() || s.transcript || s.extractedText || (s.attachmentIds || []).length
    );
    if (resumePlanId) resumePlan(resumePlanId);
    else if (hasSeed) startPlan(s);
    else pushAssistant(GREETING);

    return () => {
      sessionRef.current += 1;
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // The studio writes its own plan id into the URL once generation starts; only a different plan reloads.
  useEffect(() => {
    if (!open || !resumePlanId || resumePlanId === sessionPlanIdRef.current) return;
    ignoreSeedStyleRef.current = false;
    resetSession();
    resumePlan(resumePlanId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumePlanId]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const el = overlayRef.current;
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!el) return undefined;
    const apply = () => {
      const height = vv?.height ? `${Math.round(vv.height)}px` : '100dvh';
      el.style.height = height;
      el.style.maxHeight = height;
    };
    apply();
    if (!vv) return undefined;
    vv.addEventListener('resize', apply);
    vv.addEventListener('scroll', apply);
    return () => {
      vv.removeEventListener('resize', apply);
      vv.removeEventListener('scroll', apply);
    };
  }, [open]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, question, busy, job]);

  const runAnswer = async (payload, userLabel, { force = false, autoStart } = {}) => {
    if (!plan?.id || busy || executed) return;
    if (userLabel) pushUser(userLabel);
    setBusy(true);
    let next = null;
    try {
      next = await answerCreationPlan(plan.id, payload);
      if (payload.message && next.flow?.id !== plan.flow?.id) {
        pushAssistant(`Switched to **${next.flow.label}**.`);
      } else if (payload.message && next?.story_workflow?.confirm_requested) {
        pushAssistant('Confirm this version with the button in the chat.');
      } else if (payload.message) {
        pushAssistant('Updated the plan.');
      }
      const shouldAutoStart = autoStart ?? Boolean(next?.ready);
      announce(next, { force, autoStart: shouldAutoStart });
    } catch (err) {
      toast.error(formatApiError(err, 'Could not update the plan'));
      if (question) pushAssistant(question.prompt);
    } finally {
      setBusy(false);
    }
    // A chat edit mid-cast / mid-course only updates the plan; in-progress steps keep their buttons.
    const midCast = usesCast(next) && castStage(next) !== 'overview' && Boolean(payload.message);
    const midCourse = usesCourse(next) && Boolean(course?.plan || course?.explain_job_id) && Boolean(payload.message);
    const shouldAutoStart = autoStart ?? Boolean(next?.ready);
    const unifiedStoryPlan = next?.flow?.id === 'unified_story';
    if (shouldAutoStart && next?.ready && !midCast && !midCourse && !unifiedStoryPlan) await handleExecute('auto', next);
    return next;
  };

  const savePlanField = async (path, value, label) => {
    const shown = label || formatFieldValue(path, value);
    const next = await runAnswer({ field: path, value }, `Change ${fieldLabel(path)} to ${shown}`, { autoStart: false });
    if (next) setEditingField(null);
  };

  const answerQuestion = (value, label) => {
    if (!question) return;
    runAnswer({ field: question.field, value }, label ?? String(value));
  };

  const multiChoice = isMultiChoice(question);
  const otherAllowed = canAnswerOther(question);
  const multiAnswerCount = multiChoice
    ? buildMultiAnswer(question, choicePicks, otherOpen ? otherText : '').value.length
    : 0;

  const toggleChoicePick = (value) => {
    setChoicePicks((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
  };

  const pickOption = (opt) => {
    if (multiChoice) {
      toggleChoicePick(opt.value);
      return;
    }
    setOptionsPopupOpen(false);
    answerQuestion(opt.value, opt.label);
  };

  const optionSelected = (opt) => (multiChoice ? choicePicks.includes(opt.value) : Boolean(opt.isDefault));
  const imageOptions = optionsHaveImages(question);
  const { inline: inlineOptions, hidden: hiddenOptionCount } = splitInlineOptions(question?.options, {
    limit: imageOptions ? INLINE_IMAGE_OPTIONS : INLINE_TEXT_OPTIONS,
    picks: choicePicks,
  });

  const renderUnavailableOption = (opt) => (
    <button
      key={String(opt.value)}
      type="button"
      disabled
      aria-disabled="true"
      title={[opt.disabled_reason, opt.description].filter(Boolean).join(' — ') || undefined}
      data-testid="create-plan-option-unavailable"
      className="px-3 py-1.5 rounded-full text-xs border border-dashed border-white/10 bg-transparent text-[#6B7280] cursor-not-allowed"
    >
      <span className="line-through">{opt.label}</span>
      {opt.disabled_reason ? (
        <span className="ml-1.5 text-[9px] uppercase tracking-wide no-underline opacity-80">{opt.disabled_reason}</span>
      ) : null}
      {opt.description ? (
        <span className="block text-left text-[10px] opacity-60 max-w-[240px] whitespace-normal line-through">
          {opt.description}
        </span>
      ) : null}
    </button>
  );

  const renderOptionChip = (opt) => {
    if (opt.disabled) return renderUnavailableOption(opt);
    const selected = optionSelected(opt);
    return (
      <button
        key={String(opt.value)}
        type="button"
        title={opt.description || undefined}
        aria-pressed={multiChoice ? selected : undefined}
        onClick={() => pickOption(opt)}
        className={`${imageOptions ? 'pl-1 pr-3 py-1' : 'px-3 py-1.5'} rounded-full text-xs cursor-pointer border transition-colors inline-flex items-center gap-1.5 ${
          selected
            ? 'border-[#2DD4BF]/70 bg-[#2DD4BF]/15 text-[#2DD4BF]'
            : 'border-white/15 bg-white/[0.04] text-[#E5E7EB] hover:border-[#2DD4BF]/50 hover:text-white'
        }`}
      >
        {imageOptions ? <OptionThumb opt={opt} className="w-7 h-7 rounded-full" /> : null}
        {multiChoice && selected ? <Check className="w-3 h-3 shrink-0" /> : null}
        <span className={opt.description ? 'text-left' : undefined}>
          {opt.label}
          {opt.isDefault ? (
            <span className="ml-1.5 text-[9px] uppercase tracking-wide opacity-80">suggested</span>
          ) : null}
          {opt.description ? (
            <span className="block text-[10px] opacity-70 max-w-[240px] whitespace-normal">{opt.description}</span>
          ) : null}
        </span>
      </button>
    );
  };

  const renderOptionCard = (opt) => {
    if (opt.disabled) return renderUnavailableOption(opt);
    const selected = optionSelected(opt);
    return (
      <button
        key={String(opt.value)}
        type="button"
        title={opt.description || undefined}
        aria-pressed={multiChoice ? selected : undefined}
        onClick={() => pickOption(opt)}
        className={`group relative flex flex-col rounded-xl border text-left overflow-hidden cursor-pointer transition-colors ${
          selected
            ? 'border-[#2DD4BF] ring-1 ring-[#2DD4BF]/60 bg-[#2DD4BF]/10'
            : 'border-white/10 bg-white/[0.03] hover:border-[#2DD4BF]/50'
        }`}
      >
        {imageOptions ? <OptionThumb opt={opt} className="w-full aspect-square" /> : null}
        <span className="px-2.5 py-2 text-xs text-white leading-snug">
          {opt.label}
          {opt.isDefault ? (
            <span className="block text-[9px] uppercase tracking-wide text-[#2DD4BF]">suggested</span>
          ) : null}
          {opt.description ? (
            <span className="block text-[10px] text-[#9CA3AF] whitespace-normal">{opt.description}</span>
          ) : null}
        </span>
        {selected ? (
          <span className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-[#2DD4BF] text-[#06080D] flex items-center justify-center">
            <Check className="w-3 h-3" />
          </span>
        ) : null}
      </button>
    );
  };

  /** Story-format pick: narrator / dialogue / both — with a short sample screenplay each. */
  const renderFormatSampleCard = (opt) => {
    if (opt.disabled) return renderUnavailableOption(opt);
    const selected = optionSelected(opt);
    const key = String(opt.value);
    const expanded = formatSampleExpanded === key;
    const sample = String(opt.sample || '').trim();
    return (
      <div
        key={key}
        data-testid="create-plan-format-sample"
        className={`rounded-2xl border text-left overflow-hidden transition-colors ${
          selected
            ? 'border-[#2DD4BF]/70 bg-[#2DD4BF]/10'
            : 'border-white/10 bg-white/[0.03]'
        }`}
      >
        <button
          type="button"
          onClick={() => pickOption(opt)}
          className="w-full px-3.5 py-3 text-left cursor-pointer hover:bg-white/[0.03]"
        >
          <span className="flex items-start gap-2">
            <span className="min-w-0 flex-1">
              <span className="text-xs font-semibold text-white">
                {opt.label}
                {opt.isDefault ? (
                  <span className="ml-1.5 text-[9px] uppercase tracking-wide text-[#2DD4BF]">suggested</span>
                ) : null}
              </span>
              {opt.description ? (
                <span className="block text-[11px] text-[#9CA3AF] mt-0.5 leading-snug">{opt.description}</span>
              ) : null}
            </span>
            {selected ? <Check className="w-4 h-4 text-[#2DD4BF] shrink-0 mt-0.5" /> : null}
          </span>
        </button>
        {sample ? (
          <div className="border-t border-white/10 px-3.5 py-2.5 space-y-1.5">
            <button
              type="button"
              onClick={() => setFormatSampleExpanded((prev) => (prev === key ? '' : key))}
              className="text-[10px] uppercase tracking-wide text-[#7DD3FC] hover:text-white cursor-pointer flex items-center gap-1"
            >
              <BookOpen className="w-3 h-3" />
              {expanded ? 'Hide sample scene' : 'Show sample scene'}
            </button>
            <pre
              className={`text-[11px] leading-relaxed text-[#E5E7EB] whitespace-pre-wrap font-sans ${
                expanded ? '' : 'line-clamp-4'
              }`}
            >
              {sample}
            </pre>
          </div>
        ) : null}
      </div>
    );
  };

  const submitChoiceAnswer = () => {
    if (multiChoice) {
      const { value, label } = buildMultiAnswer(question, choicePicks, otherOpen ? otherText : '');
      if (value.length) answerQuestion(value, label);
      return;
    }
    const text = otherText.trim();
    if (text) answerQuestion(text, text);
  };

  // ── Style image upload for visual_style question ────────────────────────
  const handleStyleImageFiles = useCallback(async (files) => {
    if (!files?.length) return;
    setStyleUploading(true);
    for (const file of Array.from(files).slice(0, 4)) {
      const localId = `si-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      const previewUrl = URL.createObjectURL(file);
      setStyleImages((prev) => [...prev, { id: localId, previewUrl, status: 'uploading', styleDesc: '' }]);
      try {
        const { attachment, projectId: pid } = await uploadProductionAttachment(file, {
          attachmentType: 'image',
          projectId: styleProjectIdRef.current || plan?.id,
          source: 'attach',
        });
        if (pid) styleProjectIdRef.current = pid;
        setStyleImages((prev) =>
          prev.map((img) => img.id === localId ? { ...img, id: attachment.id, status: 'analyzing' } : img)
        );
        try {
          const done = await pollAttachmentUntilDone(attachment.id, { intervalMs: 1500, timeoutMs: 75000 });
          const desc = styleNoteFromAnalysis(done?.analysis);
          if (!desc) {
            const errHint = String(done?.error || '').trim();
            toast.error(
              errHint || 'Could not detect a visual style from this image — try again or type one.'
            );
          }
          setStyleImages((prev) => {
            const next = prev.map((img) =>
              img.id === attachment.id ? { ...img, status: 'done', styleDesc: desc } : img
            );
            const combined = next.map((i) => i.styleDesc).filter(Boolean).join(' ').slice(0, 800);
            if (combined) {
              setDetectedStyleNote(combined);
              // Keep the popup textarea in sync (don't leave a stale edit draft).
              setStyleNoteEditing(combined);
            }
            return next;
          });
        } catch {
          toast.error('Style analysis timed out — try again or type a description.');
          setStyleImages((prev) =>
            prev.map((img) => img.id === attachment.id ? { ...img, status: 'done', styleDesc: '' } : img)
          );
        }
      } catch (err) {
        toast.error(formatApiError(err, `Could not upload ${file.name}`));
        setStyleImages((prev) => prev.filter((img) => img.id !== localId));
      }
    }
    setStyleUploading(false);
  }, [plan?.id]);

  const removeStyleImage = useCallback((id) => {
    setStyleImages((prev) => {
      const next = prev.filter((img) => img.id !== id);
      const combined = next.map((i) => i.styleDesc).filter(Boolean).join(' ');
      setDetectedStyleNote(combined.slice(0, 800));
      return next;
    });
  }, []);

  const submitSongGeneration = async () => {
    if (!plan?.id || busy || executed) return;
    const prompt = songPrompt.trim();
    const lyrics = songInstrumental ? '' : songLyrics.trim();
    if (!prompt && !lyrics) return;
    pushUser(
      `Generate the song with AI: ${prompt || 'use my lyrics'}${songInstrumental ? ' (instrumental)' : ''}${
        lyrics ? ' — with my lyrics' : ''
      }`
    );
    setBusy(true);
    try {
      const next = await generateCreationAudio(plan.id, { prompt, lyrics, instrumental: songInstrumental });
      setPlan(next);
      setSongFormOpen(false);
      pushAssistant("Composing your song with AI — this usually takes 1–3 minutes. I'll continue as soon as it's ready.");
    } catch (err) {
      toast.error(formatApiError(err, 'Could not start song generation'));
    } finally {
      setBusy(false);
    }
  };

  const submitScriptGeneration = async (promptOverride) => {
    if (!plan?.id || busy || executed) return;
    const prompt = String(promptOverride ?? scriptPrompt ?? '').trim();
    const hadScript = Boolean(String(plan?.input?.script || '').trim());
    pushUser(
      hadScript
        ? prompt
          ? `Regenerate the script: ${prompt}`
          : 'Regenerate the script with AI'
        : prompt
          ? `Generate the script with AI: ${prompt}`
          : 'Generate the script with AI'
    );
    setBusy(true);
    try {
      const { plan: next, script } = await generateCreationScript(plan.id, { prompt });
      setPlan(next);
      setScriptFormOpen(false);
      setScriptPrompt('');
      const preview = String(script || '').trim();
      pushAssistant(
        preview
          ? `${hadScript ? "Here's the updated script" : "Here's a script I wrote"}:\n\n${
              preview.length > 1400 ? `${preview.slice(0, 1400)}…` : preview
            }`
          : hadScript
            ? "I've updated the script — review it in the plan, or paste your own."
            : "I've filled in a script — review it in the plan, or paste your own."
      );
      if (editingField === 'script') setEditingField(null);
    } catch (err) {
      toast.error(formatApiError(err, hadScript ? 'Could not regenerate the script' : 'Could not generate a script'));
    } finally {
      setBusy(false);
    }
  };

  const skipQuestion = () => {
    if (!question || question.required) return;
    const label = question.default_label ? `Skip — use ${question.default_label}` : 'Skip';
    runAnswer({ field: question.field, skip: true }, label);
  };

  const handleComposerSend = () => {
    const text = composer.trim();
    if (!text || busy || castBusy || executed) return;
    setComposer('');
    if (!plan) {
      startPlan({ text });
      return;
    }
    if (question && question.type !== 'upload' && question.type !== 'voice') {
      answerQuestion(text, text);
    } else {
      runAnswer({ message: text }, text, { force: true });
    }
  };

  const handleUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length || !question || !plan) return;
    setUploadBusy(true);
    try {
      const attachmentType = question.accept === 'file' ? 'document' : question.accept;
      const ids = [];
      for (const file of files) {
        // eslint-disable-next-line no-await-in-loop
        const { attachment } = await uploadProductionAttachment(file, {
          attachmentType,
          projectId: plan.production_project_id || undefined,
        });
        ids.push(attachment.id);
      }
      await runAnswer(
        { field: question.field, value: { attachment_ids: ids } },
        `Uploaded ${files.map((f) => f.name).join(', ')}`
      );
    } catch (err) {
      toast.error(formatApiError(err, 'Upload failed'));
    } finally {
      setUploadBusy(false);
    }
  };

  const startJobPolling = (jobId, { quietIfDone = false } = {}) => {
    stopPolling();
    let firstTick = true;
    let inFlight = false;
    let finished = false;
    const tick = async () => {
      // Slow responses must not overlap, or every in-flight tick reports the same terminal status.
      if (inFlight || finished) return;
      inFlight = true;
      const quiet = quietIfDone && firstTick;
      firstTick = false;
      try {
        const data = await fetchJob(jobId);
        if (finished) return;
        setJob(data);
        if (REVIEW_JOB_STATES.includes(data?.status)) {
          finished = true;
          stopPolling();
          setReelsRefreshKey((k) => k + 1);
          setReelsBusy((prev) => (prev === 'retry-split' ? '' : prev));
          const reviewKey = `${jobId}:awaiting_review`;
          if (reelsAfterRetranscribeRef.current) {
            reelsAfterRetranscribeRef.current = false;
            setReelsBusy('');
            if (!quiet) {
              pushAssistant(
                'Re-transcription done. Review the new subtitles, then choose title / subtitle placement for the first N seconds.'
              );
              setReelsDialog({ kind: 'transcripts' });
            }
            return;
          }
          if (reelsReviewAnnouncedRef.current !== reviewKey) {
            reelsReviewAnnouncedRef.current = reviewKey;
            if (!quiet) {
              pushAssistant(
                'Analysis ready. Detecting noise and cleaning audio by type, then I will re-transcribe and show subtitles in chat.'
              );
              void runReelsAudioPolishRef.current?.({ auto: true, announceKey: reviewKey });
            }
          }
          return;
        }
        if (TERMINAL_JOB_STATES.includes(data?.status)) {
          const isScriptBasedJob = (plan?.flow?.id || execution?.flow_id) === 'script_based'
            || data?.job_type === 'script_based_pipeline';
          const msgLower = String(data?.message || '').toLowerCase();
          const orphanScriptBased = isScriptBasedJob
            && data.status === 'completed'
            && (
              Number(data.progress) < 100
              || msgLower.includes('generating image')
              || msgLower.includes('image ready — scene')
              || msgLower.includes('timing ready')
              || msgLower.includes('whisperx')
            );
          // Keep polling so GET /jobs can finalize orphaned script-based rows (e.g. completed@57%).
          if (orphanScriptBased) {
            return;
          }
          finished = true;
          stopPolling();
          setReelsRefreshKey((k) => k + 1);
          setReelsBusy((prev) => (prev === 'retry-split' ? '' : prev));
          if (quiet) return;
          // After a re-cut, polished export may already be queued — follow that job.
          if (
            data.status === 'completed'
            && (usesReels(plan) || usesReels(execution))
            && reelsProjectId
          ) {
            try {
              const { data: projPayload } = await api.get(`/reels-shorts/projects/${reelsProjectId}`);
              const renderJob = projPayload?.render_job;
              const rs = String(renderJob?.status || '').toLowerCase();
              if (renderJob?.id && ['pending', 'running'].includes(rs)) {
                pushAssistant(
                  'Edit plan ready — exporting the polished vertical reel next. Prior exports stay in Export history on the right.',
                );
                startJobPolling(renderJob.id);
                return;
              }
            } catch {
              /* fall through to default message */
            }
          }
          {
            let latestOutputs = outputs;
            try {
              const refreshed = await refreshOutputs();
              if (refreshed) latestOutputs = refreshed;
            } catch {
              /* keep current outputs */
            }
            const readyVideoUrl = resolvedFinalVideoUrl(latestOutputs, data);
            const visualStats = sceneVisualStats(latestOutputs);
            let doneMsg = 'Your video is ready — watch it in the Output panel, export the MP4, or publish it to YouTube or Instagram. If scenes are still images, I can convert them to AI videos with multi-speaker focus.';
            if (visualStats.incomplete) {
              doneMsg = incompleteImagesMessage(visualStats);
            } else if (usesReels(plan) || usesReels(execution)) {
              doneMsg = 'Your reel cut is ready — open the editor from the right panel, or export / publish from Output. Use Re-cut polished reel to try again while keeping old MP4s in history.';
            } else if ((plan?.flow?.id || execution?.flow_id) === 'advertisement' || data?.job_type === 'advertisement_video') {
              doneMsg = readyVideoUrl
                ? 'Your advertisement is ready — watch it in the Output panel.'
                : 'Gemini finished, but the video file is not available yet. Check Output in a moment.';
            } else if (!readyVideoUrl) {
              doneMsg = isScriptBasedJob
                ? 'Scenes and images are ready — compiling the final MP4 automatically (retries up to 3 times). Export unlocks in Output when it’s ready; no Open project needed.'
                : 'Pipeline finished — open the Output panel. Export activates when a compiled MP4 is available.';
            }
            pushAssistant(
              data.status === 'completed'
                ? doneMsg
                : `The pipeline ${data.status === 'cancelled' ? 'was cancelled' : 'failed'}: ${jobFailureReason(data)}`
            );
          }
        }
      } catch {
        /* transient */
      } finally {
        inFlight = false;
      }
    };
    tick();
    pollRef.current = setInterval(tick, 3000);
  };

  // `autoStart`: the flow page starts generation itself once nothing it needs is missing
  // (Execute plan); otherwise it only asks the missing details and waits for the user.
  const openHandoff = (exec, flowId, { autoStart = false } = {}) => {
    const handoff = exec?.handoff;
    if (!handoff) return;
    const flow = STORY_FLOWS.find((f) => f.id === flowId) || STORY_FLOWS.find((f) => f.id === 'narration');
    onClose?.();
    navigate(handoff.route, {
      state: { storyFlow: storyFlowNavigationState(flow), ...handoff.state, agentAutoStart: autoStart },
    });
    onTaskCreated?.();
  };

  const openProject = () => {
    if (!execution) return;
    if (usesReels(execution) && job?.status === 'awaiting_review') {
      setReelsDialog({ kind: 'transcripts' });
      pushAssistant('Review subtitles here, then set title / subtitle placement — no need to leave Create Agent.');
      return;
    }
    if (execution.mode === 'handoff') {
      openHandoff(execution, execution.flow_id, { autoStart: false });
      return;
    }
    const flowId = execution.flow_id || plan?.flow?.id || '';
    const projectId =
      execution.project_id
      || outputs?.narration_project_id
      || outputs?.story_id
      || '';
    const route =
      outputs?.editor_url
      || projectEditorPath(flowId, projectId)
      || execution.route;
    if (!route) {
      toast.error('Project editor is not ready yet');
      return;
    }
    onClose?.();
    navigate(route);
    onTaskCreated?.();
  };

  const reelsProjectId =
    reelsPlan
      ? (execution?.project_id || outputs?.narration_project_id || null)
      : null;

  const runReelsRetranscribe = async ({ fromPolish = false } = {}) => {
    if (!reelsProjectId || reelsBusy === 'retranscribe' || reelsBusy === 'clean') return;
    setReelsBusy('retranscribe');
    try {
      const language = String(
        plan?.input?.language || execution?.input?.language || 'auto',
      ).trim().toLowerCase() || 'auto';
      if (!fromPolish) {
        pushUser('Retranscribe');
        pushAssistant('Re-transcribing cleaned audio for fresher subtitles…');
      }
      const { data } = await api.patch(`/reels-shorts/projects/${reelsProjectId}/language`, {
        language,
        retranscribe: true,
      });
      const nextJobId = data?.job_id || job?.id;
      reelsAfterRetranscribeRef.current = true;
      setJob({
        id: nextJobId,
        status: data?.status || 'running',
        progress: 12,
        message: `Re-transcribing in ${language}`,
      });
      toast.success('Re-transcribing…');
      if (nextJobId) startJobPolling(nextJobId);
      setReelsRefreshKey((k) => k + 1);
    } catch (err) {
      reelsAfterRetranscribeRef.current = false;
      setReelsBusy('');
      toast.error(formatApiError(err, 'Could not retranscribe'));
      pushAssistant(`Re-transcribe failed: ${formatApiError(err)}`);
    }
  };

  const runReelsRetrySplitAv = async () => {
    if (!reelsProjectId || reelsBusy === 'retry-split') return;
    setReelsBusy('retry-split');
    try {
      pushUser('Retry Split A/V');
      pushAssistant('Re-running Split A/V + transcribe to rebuild mixed and per-speaker audio tracks…');
      const { data } = await api.post(`/reels-shorts/projects/${reelsProjectId}/retry`);
      const nextJobId = data?.job_id || job?.id;
      setJob({
        id: nextJobId,
        status: data?.status || 'pending',
        progress: 0,
        message: 'Retry queued — splitting audio & video',
      });
      toast.info('Re-running Split A/V + transcribe');
      if (nextJobId) startJobPolling(nextJobId);
      setReelsRefreshKey((k) => k + 1);
    } catch (err) {
      setReelsBusy('');
      toast.error(formatApiError(err, 'Retry Split A/V failed'));
      pushAssistant(`Retry Split A/V failed: ${formatApiError(err)}`);
    }
  };

  const runReelsAudioPolish = async ({ auto = false, announceKey = '' } = {}) => {
    if (!reelsProjectId) return;
    if (auto && announceKey && reelsPolishStartedRef.current === announceKey) return;
    if (reelsBusy === 'clean' || reelsBusy === 'retranscribe') return;
    if (auto && announceKey) reelsPolishStartedRef.current = announceKey;
    setReelsBusy('clean');
    try {
      if (!auto) pushUser('Detect & clean audio');
      pushAssistant(
        auto
          ? 'Listening for noise (hum, hiss, crowd, wind…) and cleaning each clip by type…'
          : 'Detecting noise type and cleaning audio…',
      );
      const { data } = await api.post(
        `/reels-shorts/projects/${reelsProjectId}/clean-audio`,
        { preset: 'auto' },
        { timeout: 600_000 },
      );
      const snippets = data?.project?.snippets || [];
      const summary = cleanAllToast(snippets);
      toast.success(summary);
      pushAssistant(`${summary} Next: re-transcribe so subtitles match the cleaned audio.`);
      setReelsRefreshKey((k) => k + 1);
      setReelsBusy('');
      await runReelsRetranscribe({ fromPolish: true });
    } catch (err) {
      setReelsBusy('');
      toast.error(formatApiError(err, 'Could not clean audio'));
      pushAssistant(
        `Audio clean failed: ${formatApiError(err)}. You can retry Detect & clean on the right, or review subtitles as-is.`,
      );
      setReelsDialog({ kind: 'transcripts' });
    }
  };
  runReelsAudioPolishRef.current = runReelsAudioPolish;

  const runReelsAiEditor = async () => {
    if (!reelsProjectId || reelsBusy === 'editor') return;
    setReelsBusy('editor');
    try {
      const { data } = await api.post(`/reels-shorts/projects/${reelsProjectId}/editor-cut`);
      const nextJobId = data?.job_id;
      setJob({
        id: nextJobId,
        status: data?.status || 'running',
        progress: 86,
        message: 'ChatGPT editor is ordering snippets and suggesting B-ROLL',
      });
      pushUser('Generate edit');
      pushAssistant('Running the AI director — building the edit plan (order, B-roll, cleanup). Progress is on the right.');
      toast.success('Generating edit plan');
      if (nextJobId) startJobPolling(nextJobId);
      setReelsRefreshKey((k) => k + 1);
    } catch (err) {
      toast.error(formatApiError(err, 'Could not run AI editor'));
      pushAssistant(`AI editor failed to start: ${formatApiError(err)}`);
    } finally {
      setReelsBusy('');
    }
  };

  const runReelsRecut = async () => {
    if (!reelsProjectId || reelsBusy === 'recut' || reelsBusy === 'editor') return;
    setReelsBusy('recut');
    try {
      const { data } = await api.post(`/reels-shorts/projects/${reelsProjectId}/recut`, {
        auto_render: true,
        include_broll: true,
        include_subtitles: true,
      });
      const nextJobId = data?.job_id;
      setJob({
        id: nextJobId,
        status: data?.status || 'running',
        progress: 86,
        message: data?.message || 'Re-cutting polished reel on same clips',
      });
      pushUser('Re-cut polished reel');
      pushAssistant(
        data?.archived_export
          ? 'Re-cutting the same uploaded clips into a new polished vertical reel. Your previous export is saved in Export history — you can restore it anytime.'
          : 'Re-cutting the same uploaded clips into a polished vertical reel. After the edit plan, export starts automatically.',
      );
      toast.success(
        data?.archived_export
          ? 'Re-cutting — prior export saved in history'
          : 'Re-cutting polished reel',
      );
      if (nextJobId) startJobPolling(nextJobId);
      setReelsRefreshKey((k) => k + 1);
    } catch (err) {
      toast.error(formatApiError(err, 'Could not re-cut reel'));
      pushAssistant(`Re-cut failed to start: ${formatApiError(err)}`);
    } finally {
      setReelsBusy('');
    }
  };

  const openReelsEditor = (url) => {
    const path = url || (reelsProjectId ? reelsShortsEditorPath(reelsProjectId) : '');
    if (!path) return;
    window.open(path, '_blank', 'noopener,noreferrer');
  };

  const castAction = async (step, fn) => {
    if (castBusy) return null;
    const session = sessionRef.current;
    setCastBusy(step);
    try {
      const result = await fn();
      return session === sessionRef.current ? result : null;
    } finally {
      if (session === sessionRef.current) setCastBusy('');
    }
  };

  const runCastDetect = (target) =>
    castAction('detect', async () => {
      try {
        const next = await detectCastCharacters(target.id);
        setPlan(next);
        const names = (next.cast?.characters || []).map((m) => m.name);
        pushAssistant(
          names.length
            ? `I found ${names.length} character${names.length === 1 ? '' : 's'}: ${names.join(', ')}.\n\n` +
                'Build each one in the character builder — an AI portrait or your own photo, traits and personality — or let me build them all.'
            : "I couldn't find named characters in the overview. Add them in the character builder."
        );
        setCastDialog({ kind: 'builder' });
        return next;
      } catch (err) {
        pushAssistant(`I couldn't detect the characters: ${formatApiError(err, 'character detection failed')}`);
        return null;
      }
    });

  const runCastOverview = (target, { regenerate = false, text = null } = {}) =>
    castAction('overview', async () => {
      try {
        const next = await writeCastOverview(target.id, text != null ? { text } : { regenerate });
        setPlan(next);
        const cast = next.cast || {};
        if (cast.overview_source === 'script') {
          pushAssistant("I'll use your script as the story overview. Next I'll find the characters in it.");
        } else if (cast.overview_source === 'user') {
          pushAssistant('Saved your story overview.');
        } else {
          pushAssistant(
            `Here's the story overview:\n\n${excerpt(cast.overview)}\n\n` +
              "The full text is on the right — edit it any time. Next I'll find the characters."
          );
        }
        return next;
      } catch (err) {
        pushAssistant(`I couldn't write the story overview: ${formatApiError(err, 'story writer unavailable')}`);
        return null;
      }
    });

  /** Run the next cast step: write the overview, detect characters, or open the builder / voice popup. */
  const continueCast = async (target = plan) => {
    const stage = castStage(target);
    if (stage === 'overview') {
      const next = await runCastOverview(target);
      if (next && castStage(next) === 'characters' && !next.cast?.characters?.length) await runCastDetect(next);
    } else if (stage === 'characters') {
      if (target.cast?.characters?.length) setCastDialog({ kind: 'builder' });
      else await runCastDetect(target);
    } else if (stage === 'voices') {
      setCastDialog({ kind: 'voices' });
    }
  };

  const unifiedCastOpenedRef = useRef('');
  const continueCastRef = useRef(continueCast);
  continueCastRef.current = continueCast;

  const saveStoryFormat = async (recommendation, action) => {
    if (!plan?.id) return;
    setBusy(true);
    try {
      const data = await updateStoryFormat(plan.id, {
        action,
        format_id: plan?.story_workflow?.story_format?.format_id,
        recommendation,
      });
      if (data?.plan) setPlan(data.plan);
    } catch (err) {
      toast.error(formatApiError(err, 'Could not save the storytelling format'));
    } finally {
      setBusy(false);
    }
  };

  const applyScreenplayResult = (data, fallback) => {
    if (data?.plan) setPlan(data.plan);
  };

  const runScreenplay = async (work, fallback) => {
    if (!plan?.id) return;
    setBusy(true);
    try {
      applyScreenplayResult(await work(plan.id));
    } catch (err) {
      toast.error(formatApiError(err, fallback));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!open || executed || !unifiedStory || !plan?.id) return undefined;
    const state = plan?.story_workflow?.state;
    if (state !== 'analyzing_sources') return undefined;
    let cancelled = false;
    const timer = setInterval(async () => {
      try {
        const { plan: latest } = await fetchCreationPlan(plan.id);
        if (!cancelled && latest) setPlan(latest);
      } catch {
        /* The next poll retries. */
      }
    }, 2000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [open, executed, unifiedStory, plan?.id, plan?.story_workflow?.state]);

  useEffect(() => {
    if (!open || executed || !plan?.id || castDialog) return;
    if (plan.flow?.id !== 'unified_story' || plan.next_question) return;
    if (!usesCast(plan) || !castStage(plan) || castStage(plan) === 'done') return;
    if (unifiedCastOpenedRef.current === plan.id) return;
    unifiedCastOpenedRef.current = plan.id;
    continueCastRef.current(plan);
  }, [open, executed, plan, castDialog]);

  const saveCastOverview = async (text) => {
    const trimmed = String(text || '').trim();
    if (trimmed.length < 40) {
      toast.error('Write at least a few sentences for the overview');
      return false;
    }
    pushUser('Updated the story overview');
    const next = await runCastOverview(plan, { text: trimmed });
    if (next && !next.cast?.characters?.length) await runCastDetect(next);
    return Boolean(next);
  };

  const regenerateCastOverview = async () => {
    pushUser('Rewrite the story overview');
    const next = await runCastOverview(plan, { regenerate: true });
    if (next) await runCastDetect(next);
  };

  const onCastCharactersDone = (next, { auto = false, failed = 0 } = {}) => {
    setCastDialog(null);
    const members = next.cast?.characters || [];
    const withLook = members.filter((m) => m.image_url).length;
    pushAssistant(
      `${auto ? 'I built' : 'Your'} ${members.length} character${members.length === 1 ? '' : 's'}${auto ? '' : ' are ready'} — ` +
        `${withLook} with a portrait.` +
        (failed ? ` ${failed} portrait${failed === 1 ? '' : 's'} failed; redo them from the character builder.` : '') +
        '\n\nNow let’s give each one a voice — pick from the suggestions or let me assign them.'
    );
    setCastDialog({ kind: 'voices' });
  };

  const openCharacterBuilder = async (people = []) => {
    if (!plan?.id) return;
    let latest = plan;
    const have = new Set((latest.cast?.characters || []).map((member) => String(member.name || '').trim().toLowerCase()));
    for (const person of people) {
      const row = typeof person === 'string' ? { name: person } : (person || {});
      const clean = String(row.display_name || row.name || '').trim();
      if (!clean || have.has(clean.toLowerCase())) continue;
      const traits = Array.isArray(row.traits) ? row.traits : Array.isArray(row.personality_traits) ? row.personality_traits : [];
      try {
        latest = await addCastCharacter(plan.id, {
          name: clean,
          ...(row.description ? { description: row.description } : {}),
          ...(traits.length ? { traits } : {}),
        });
        have.add(clean.toLowerCase());
      } catch (err) {
        toast.error(formatApiError(err, `Could not add ${clean}`));
      }
    }
    if (latest && latest !== plan) setPlan(latest);
    setCastDialog({ kind: 'builder' });
  };

  const lockBuilderCast = async (next) => {
    const versionId = next?.story_workflow?.draft_version_id;
    if (!next?.id || !versionId) return next;
    const loaded = await fetchStoryContext(next.id);
    const members = new Map(
      (next.cast?.characters || []).map((member) => [String(member.name || '').trim().toLowerCase(), member]),
    );
    const entities = (loaded?.context?.graph?.entities || [])
      .filter((entity) => entity?.entity_id && entity.review_status !== 'rejected' && entity.story_character !== false)
      .map((entity) => {
        const member = members.get(String(entity.display_name || '').trim().toLowerCase());
        const photo = String(member?.image_url || '').trim();
        const voiceId = String(member?.voice?.voice_id || '').trim();
        if (!member || !photo || !voiceId) {
          return { entity_id: entity.entity_id, review_status: 'proposed', story_character: true };
        }
        return {
          entity_id: entity.entity_id,
          review_status: 'confirmed',
          story_character: true,
          voice: { provider: member.voice?.provider || 'elevenlabs', voice_id: voiceId },
          reference_frames: Object.fromEntries(REFERENCE_FRAMES.map((key) => [key, photo])),
        };
      });
    const data = await lockStoryCharacters(next.id, { version_id: versionId, entities });
    return data?.plan || next;
  };

  const onCastVoicesDone = async (next, { auto = false } = {}) => {
    setCastDialog(null);
    if (next?.flow?.id === 'unified_story') {
      let locked = next;
      try {
        locked = await lockBuilderCast(next);
        if (locked) setPlan(locked);
      } catch (err) {
        toast.error(formatApiError(err, 'Finish each character portrait and voice before the screenplay'));
      }
      pushAssistant(
        `${auto ? 'I allocated the voices' : 'Voices saved'}:\n${voiceSummary(locked)}\n\n` +
          'Character views and voices are locked in the character builder.'
      );
      return;
    }
    pushAssistant(`${auto ? 'I allocated the voices' : 'Voices saved'}:\n${voiceSummary(next)}\n\nGenerating the story and scenes now…`);
    await executeRef.current('auto', next);
  };

  const autoBuildFromChat = () =>
    castAction('auto-build', async () => {
      pushUser('Build the characters for me');
      try {
        const { plan: next, failed } = await autoBuildCast(plan.id, plan.cast?.characters || [], {
          onPlan: setPlan,
          onPortraitError: (member, err) => toast.error(formatApiError(err, `Couldn't draw ${member.name}`)),
        });
        onCastCharactersDone(next, { auto: true, failed });
      } catch (err) {
        pushAssistant(`I couldn't build the characters: ${formatApiError(err, 'character builder failed')}`);
      }
    });

  const autoVoicesFromChat = async () => {
    pushUser('Assign the voices for me');
    const next = await castAction('auto-voices', async () => {
      try {
        const updated = await assignCastVoices(plan.id, { auto: true, confirm: true });
        setPlan(updated);
        return updated;
      } catch (err) {
        pushAssistant(`I couldn't assign the voices: ${formatApiError(err, 'voice catalog unavailable')}`);
        return null;
      }
    });
    if (!next) return;
    if (castStage(next) === 'done') await onCastVoicesDone(next, { auto: true });
    else {
      pushAssistant('Some characters still need a voice — pick them in the voice popup.');
      setCastDialog({ kind: 'voices' });
    }
  };

  const resolveCourseState = useCallback(() => {
    if (course?.plan?.curriculum?.topics?.length) return course;
    if (plan?.id) {
      const stored = loadCourseState(plan.id);
      if (stored?.plan?.curriculum?.topics?.length) return stored;
    }
    return course;
  }, [course, plan?.id]);

  const startLessonArtifacts = async ({ language, tone } = {}) => {
    if (!plan?.id) {
      toast.error('No plan loaded');
      return;
    }
    if (courseBusy) return;
    const active = resolveCourseState();
    if (!active?.plan?.curriculum?.topics?.length) {
      toast.error('Plan the course first — curriculum is missing');
      pushAssistant('I need a planned curriculum before generating lesson artifacts. Hit **Analyze & plan course**.');
      return;
    }
    if (active !== course) setCourse(active);
    const lang = language || active.language || plan.input?.language || 'en';
    const narrationTone = tone || active.tone || 'serious';
    setCourseBusy('explain');
    setCourseDialog(null);
    try {
      const next = await runCourseExplain(active, {
        language: lang,
        tone: narrationTone,
        title: plan.input?.title || active.plan?.title,
      });
      setCourse(next);
      pushUser(`Generate lesson artifacts (${lang}, ${narrationTone === 'genz' ? 'Funny GenZ' : 'Serious'})`);
      pushAssistant(
        'Queued lesson artifacts for every topic and subtopic. Progress is on the right — I’ll open alignment when they finish.'
      );
    } catch (err) {
      toast.error(formatApiError(err, 'Could not start artifact generation'));
      pushAssistant(`Artifact generation failed to start: ${formatApiError(err)}`);
      setCourseDialog({ kind: 'narration' });
    } finally {
      setCourseBusy('');
    }
  };

  const continueCourse = async (target = plan) => {
    if (!target?.id || courseBusy) return;
    const active = resolveCourseState();
    const stage = courseStage(active);
    if (stage === 'plan' || !active?.plan) {
      setCourseBusy('plan');
      pushUser('Plan the course');
      let planned = null;
      try {
        planned = await runCoursePlan(target);
        setCourse(planned);
        const topics = planned.plan?.curriculum?.topics?.length || 0;
        pushAssistant(
          `Course planned${topics ? ` with ${topics} topic${topics === 1 ? '' : 's'}` : ''}. ` +
            'Starting lesson artifacts with your plan language — progress is on the right.'
        );
        setCourseBusy('explain');
        const language = planned.language || target.input?.language || 'en';
        const tone = planned.tone || 'serious';
        const explained = await runCourseExplain(planned, { language, tone });
        setCourse(explained);
        pushUser(`Generate lesson artifacts (${language}, ${tone === 'genz' ? 'Funny GenZ' : 'Serious'})`);
        pushAssistant(
          'Queued lesson artifacts for every topic and subtopic. Progress is on the right — I’ll open alignment when they finish.'
        );
      } catch (err) {
        toast.error(formatApiError(err, 'Could not continue the course'));
        pushAssistant(`Course step failed: ${formatApiError(err, 'planner unavailable')}`);
        if (planned?.plan) {
          setCourse(planned);
          setCourseDialog({ kind: 'narration' });
        }
      } finally {
        setCourseBusy('');
      }
      return;
    }
    if (!active.explain_job_id) {
      await startLessonArtifacts({
        language: active.language || target.input?.language || 'en',
        tone: active.tone || 'serious',
      });
      return;
    }
    if (active.explain_status === 'completed' && active.align_status !== 'completed') {
      await startCourseAlignment(active, target);
      return;
    }
    if (active.align_status === 'completed') {
      setAlignPopupOpen(true);
      pushAssistant('Alignment is ready — preview and pipeline details are on the right.');
      return;
    }
    pushAssistant('Artifact generation is still running — status is on the right.');
  };

  const confirmCourseNarration = async ({ language, tone }) => {
    await startLessonArtifacts({ language, tone });
  };

  const handleExecute = async (mode = 'auto', target = plan) => {
    if (!target?.can_execute || executed) return;
    // User-facing starts always go through api so Task History gets a linked agent plan.
    // (Backend may still return handoff for a few flows; those open with nav state, not bookmarks.)
    const execMode = mode === 'handoff' ? 'api' : mode;
    if (usesCast(target) && castStage(target) !== 'done') {
      await continueCast(target);
      return;
    }
    if (usesCourse(target) && courseStage(course) !== 'done') {
      await continueCourse(target);
      return;
    }
    setBusy(true);
    try {
      const { execution: exec, plan: updated } = await executeCreationPlan(target.id, { mode: execMode });
      if (blankAgentHandoff(exec)) {
        pushAssistant(
          'Generate story stays in this chat. The story service did not start a project, so I did not open a new chat.'
        );
        toast.error('Story generation did not start');
        return;
      }
      setPlan(updated);
      setExecution(exec);
      if (exec.mode === 'handoff') {
        // Prefill tutorial page with the joined narration script when we have one.
        const script = courseNarrationScript(course, updated.input?.title || '');
        if (script && exec.handoff?.state) {
          const state = {
            ...exec.handoff.state,
            prefill: { ...(exec.handoff.state.prefill || {}), fullScript: script, tutorialTopic: updated.input?.brief || '' },
            recommendedParams: {
              ...(exec.handoff.state.recommendedParams || {}),
              full_script: script,
              tutorial_topic: updated.input?.brief || '',
            },
          };
          toast.success(`Opening ${updated.flow.label}`);
          onClose?.();
          navigate(exec.handoff.route, {
            state: {
              storyFlow: storyFlowNavigationState(
                STORY_FLOWS.find((f) => f.id === updated.flow.id) || STORY_FLOWS.find((f) => f.id === 'narration')
              ),
              ...state,
              agentAutoStart: mode !== 'handoff',
            },
          });
          onTaskCreated?.();
          return;
        }
        toast.success(`Opening ${updated.flow.label}`);
        openHandoff(exec, updated.flow.id, { autoStart: mode !== 'handoff' });
        return;
      }
      syncUrlParams({ planId: updated.id, storyId: exec.project_id || null });
      const isAdvertisement = (updated.flow?.id || exec.flow_id) === 'advertisement';
      pushAssistant(
        isAdvertisement
          ? 'Creating your advertisement with Gemini from the details we collected. Gemini renders one short spot (up to 8 seconds). It will show in Output when it is ready.'
          : `Started **${updated.flow.label}** — project created.` +
            (exec.job_id
              ? usesReels(updated) || usesReels(exec)
                ? ' Tracking progress on the right. When transcripts are ready we will review them here, then run the AI editor.'
                : ' Tracking the pipeline below.'
              : ' Open it to continue the next step.')
      );
      if (exec.job_id) startJobPolling(exec.job_id);
      onTaskCreated?.();
    } catch (err) {
      const msg = formatApiError(err, 'Could not execute the plan');
      toast.error(msg);
      pushAssistant(`Execution failed: ${msg}`);
    } finally {
      setBusy(false);
    }
  };

  const handleRetry = async ({ withLooks = false, continueFrom = false } = {}) => {
    if (!plan?.id || retrying) return;
    const failedStep = job?.failed_step || '';
    setRetryAction(withLooks ? 'regenerate' : continueFrom ? 'continue' : 'retry');
    setRetrying(true);
    try {
      const { plan: updated, execution: exec } = await retryCreationPlan(plan.id, {
        restart: withLooks,
        continueFrom,
      });
      setPlan(updated);
      setExecution(exec);
      setJob(null);
      setCastDialog(null);
      pushUser(
        withLooks
          ? 'Regenerate the whole video'
          : continueFrom
            ? 'Continue from where left'
            : 'Retry from the failed step'
      );
      pushAssistant(
        withLooks
          ? 'Regenerating the whole video from the start, including scene images.'
          : continueFrom || exec.resumed_from === 'continue'
            ? 'Continuing from the scenes already made. Existing images stay; only missing images and audio are generated.'
            : exec.resumed_from === 'start'
              ? 'Restarting the story from the beginning so the new portraits are used in every scene.'
              : exec.resumed_from || failedStep
                ? `Retrying — continuing from “${exec.resumed_from || failedStep}”. Finished scenes are reused.`
                : 'Retrying — finished scenes are reused.'
      );
      if (exec.job_id) startJobPolling(exec.job_id);
      onTaskCreated?.();
    } catch (err) {
      toast.error(formatApiError(err, 'Could not retry the task'));
    } finally {
      setRetrying(false);
      setRetryAction('');
    }
  };

  const handleRerender = async ({ soundDesign = false } = {}) => {
    if (!plan?.id || rerendering || convertingImages) return;
    setRerendering(true);
    try {
      const { plan: updated, execution: exec } = await rerenderCreationPlan(plan.id, { soundDesign });
      setPlan(updated);
      setExecution(exec);
      setJob(null);
      pushUser(soundDesign ? 'Add music and sound effects' : 'Re-render the final video');
      pushAssistant(
        soundDesign
          ? 'Planning background music, ambience and sound effects for each scene, generating them, then re-rendering the video.'
          : 'Re-rendering the final video from the current scenes — missing images are redrawn first.'
      );
      if (exec.job_id) startJobPolling(exec.job_id);
      onTaskCreated?.();
    } catch (err) {
      toast.error(formatApiError(err, 'Could not re-render the video'));
    } finally {
      setRerendering(false);
    }
  };

  const handleRetryExport = async () => {
    if (!plan?.id || retryingExport) return;
    const exportStats = sceneVisualStats(outputs);
    if (exportStats.incomplete) {
      const msg = incompleteImagesMessage(exportStats);
      toast.error(msg);
      pushAssistant(msg);
      return;
    }
    setRetryingExport(true);
    try {
      pushUser('Retry final video compile');
      pushAssistant('Compiling the final MP4 from your scenes again (up to 3 attempts)…');
      await exportFinalCreationPlan(plan.id);
      refreshOutputs();
      toast.message('Final video compile started');
    } catch (err) {
      toast.error(formatApiError(err, 'Could not start final video compile'));
    } finally {
      setRetryingExport(false);
    }
  };

  const handleConvertImagesToVideos = async () => {
    if (!plan?.id || convertingImages || rerendering) return;
    const pending = Number(outputs?.pending_ai_videos) || 0;
    setConvertingImages(true);
    try {
      const { plan: updated, execution: exec, pending_scenes: queued } = await convertCreationPlanImagesToVideos(plan.id);
      setPlan(updated);
      setExecution(exec);
      setJob(null);
      const n = Number(queued) || pending;
      pushUser('Convert all images to videos');
      pushAssistant(
        n
          ? `Converting ${n} still image${n === 1 ? '' : 's'} into AI video clips with multi-speaker focus, then re-rendering the final MP4. This can take a while.`
          : 'Applying multi-speaker focus and re-rendering the final video with the AI clips.'
      );
      if (exec.job_id) startJobPolling(exec.job_id);
      onTaskCreated?.();
      refreshOutputs();
    } catch (err) {
      toast.error(formatApiError(err, 'Could not convert images to videos'));
    } finally {
      setConvertingImages(false);
    }
  };

  const openEditor = (url) => {
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const executeRef = useRef(handleExecute);
  executeRef.current = handleExecute;
  const songPlanId = songRunning ? plan?.id : null;
  const songGenerationId = songRunning ? songGeneration?.id : null;

  useEffect(() => {
    if (!songPlanId || !songGenerationId) return undefined;
    let cancelled = false;
    const timer = setInterval(async () => {
      let latest;
      try {
        ({ plan: latest } = await fetchCreationPlan(songPlanId));
      } catch {
        return;
      }
      const gen = latest?.audio_generation;
      if (cancelled || !gen || gen.id !== songGenerationId || gen.status === 'running') return;
      cancelled = true;
      clearInterval(timer);
      if (gen.status === 'done') {
        const song = (latest.input?.assets || []).find((a) => a.id === gen.attachment_id);
        pushAssistant(
          gen.unused
            ? 'Your AI song finished, but I kept the track you already uploaded.'
            : `Your song is ready${song?.filename ? ` — **${song.filename}**` : ''}.`
        );
        announce(latest, { autoStart: Boolean(latest.ready) });
        if (latest.ready) executeRef.current('auto', latest);
      } else {
        const reason = gen.error || 'unknown error';
        toast.error(`Song generation failed: ${reason}`);
        pushAssistant(`I couldn't compose the song (${reason}). Try again or upload a track instead.`);
        announce(latest, { force: true });
      }
    }, 5000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [songPlanId, songGenerationId, announce, pushAssistant]);

  if (typeof document === 'undefined') return null;

  const flowLabel = plan?.flow?.label || 'Planning…';
  const missing = new Set(plan?.missing || []);
  const optionalPending = new Set(plan?.optional_pending || []);
  const pendingCount = missing.size + optionalPending.size;
  const hasSuggestionsForPending = (plan?.questions || []).some(
    (q) => !q.required || (q.suggestion != null && !String(q.field).startsWith('assets.'))
  );
  const visibleFields = PREVIEW_FIELDS.filter((path) => {
    const value = readField(plan?.input, path);
    const filled = Array.isArray(value) ? value.length > 0 : value != null && value !== '' && value !== 0;
    return filled || missing.has(path) || optionalPending.has(path);
  });
  const visualStats = sceneVisualStats(outputs);
  const jobProgress = displayJobProgress(job, outputs);
  const jobStopped = job?.status === 'failed' || job?.status === 'cancelled';
  const progressLabel = visualStats.incomplete
    ? incompleteImagesMessage(visualStats)
    : undefined;
  const jobAwaitingReview = job?.status === 'awaiting_review';
  const openProjectLabel = jobAwaitingReview
    ? (execution?.flow_id === 'reels_shorts' ? 'Review & generate edit' : 'Continue in project')
    : execution?.mode === 'handoff'
      ? `Open ${plan?.flow?.label || 'flow'}`
      : 'Open project';
  const activeTab = previewTab === 'output' && !showOutputs ? 'plan' : previewTab;

  const studio = (
    <AnimatePresence>
      {open ? (
        <motion.div
          key="create-plan-chat-studio"
          ref={overlayRef}
          // Publish dialogs render at z-[110]; drop below them while one is open.
          className={`fixed inset-0 ${publishDialog ? 'z-[100]' : 'z-[200]'} flex flex-col bg-[#07090d] h-[100dvh] max-h-[100dvh]`}
          style={{
            isolation: 'isolate',
            paddingBottom: 'env(safe-area-inset-bottom)',
            paddingLeft: 'env(safe-area-inset-left)',
            paddingRight: 'env(safe-area-inset-right)',
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          data-testid="create-plan-chat-studio"
        >
          <div className="h-12 shrink-0 border-b border-white/10 bg-[#0b0e14]/95 backdrop-blur-xl flex items-center justify-between px-3 sm:px-4">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-7 h-7 rounded-lg bg-[#2DD4BF]/15 border border-[#2DD4BF]/30 flex items-center justify-center">
                <TwinneeLogo className="w-3.5 h-3.5 text-[#2DD4BF]" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-white truncate">Create agent</p>
                <p className="text-[10px] text-[#9CA3AF] truncate max-[380px]:hidden">{flowLabel}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="lg:hidden flex items-center rounded-full border border-white/10 p-0.5 bg-black/30">
                <button
                  type="button"
                  onClick={() => setMobilePane('chat')}
                  className={`h-7 px-2.5 rounded-full text-[11px] font-semibold flex items-center gap-1 ${
                    mobilePane === 'chat' ? 'bg-white/15 text-white' : 'text-[#9CA3AF]'
                  }`}
                  data-testid="create-plan-mobile-chat"
                >
                  <MessageSquare className="w-3 h-3" />
                  Chat
                </button>
                <button
                  type="button"
                  onClick={() => setMobilePane('preview')}
                  className={`h-7 px-2.5 rounded-full text-[11px] font-semibold flex items-center gap-1 ${
                    mobilePane === 'preview' ? 'bg-white/15 text-white' : 'text-[#9CA3AF]'
                  }`}
                  data-testid="create-plan-mobile-plan"
                >
                  <Clapperboard className="w-3 h-3" />
                  {showOutputs ? 'Output' : 'Plan'}
                </button>
              </div>
              {executed ? (
                <button
                  type="button"
                  onClick={startBlank}
                  className="h-8 px-2 sm:px-3 rounded-full border border-white/10 text-[11px] font-semibold text-[#E5E7EB] hover:text-white hover:bg-white/5 flex items-center gap-1.5 cursor-pointer"
                  data-testid="create-plan-new"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">New plan</span>
                </button>
              ) : null}
              <button
                type="button"
                onClick={closeStudio}
                className="w-8 h-8 rounded-full border border-white/10 text-[#9CA3AF] hover:text-white hover:bg-white/5 flex items-center justify-center cursor-pointer"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="flex-1 min-h-0 flex flex-col lg:grid lg:grid-cols-12 overflow-hidden">
            <div
              className={`${mobilePane === 'chat' ? 'flex' : 'hidden'} lg:flex lg:col-span-5 flex-col min-h-0 h-full border-r border-white/10 bg-[#0a0d12]`}
            >
              {/* ── Style reference note banner ── */}
              {detectedStyleNote ? (
                <div className="shrink-0 border-b border-[#2DD4BF]/20 bg-[#2DD4BF]/5 px-4 py-2.5">
                  {stylePopupOpen ? null : (
                    <div className="flex items-start gap-2">
                      <Sparkles className="w-3 h-3 text-[#2DD4BF] mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-semibold text-[#2DD4BF] mb-0.5">
                          {executed ? 'Applied visual style' : 'Detected visual style'}
                        </p>
                        <p className="text-[11px] text-[#cbd5e1] leading-relaxed line-clamp-4" title={detectedStyleNote}>
                          {detectedStyleNote}
                        </p>
                      </div>
                      {!executed ? (
                        <button
                          type="button"
                          onClick={() => { setStyleNoteEditing(detectedStyleNote); setStylePopupOpen(true); }}
                          className="text-[10px] text-[#2DD4BF]/70 hover:text-[#2DD4BF] underline-offset-2 hover:underline cursor-pointer shrink-0"
                        >
                          Edit
                        </button>
                      ) : null}
                    </div>
                  )}
                </div>
              ) : null}
              <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 sm:px-5 py-5 space-y-4">
                {messages.map((m, mIdx) => {
                  // Show seed attachments inline with the very first user message
                  const isFirstUser = m.role === 'user' && mIdx === 0 && seedAttachments.length > 0;
                  return (
                    <div key={m.id} className={`flex gap-2.5 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      {m.role === 'assistant' ? (
                        <div className="w-7 h-7 rounded-full bg-[#2DD4BF]/15 border border-[#2DD4BF]/25 flex items-center justify-center shrink-0 mt-0.5">
                          <TwinneeLogo className="w-3.5 h-3.5 text-[#2DD4BF]" />
                        </div>
                      ) : null}
                      <div className="max-w-[85%] flex flex-col gap-1.5 items-end">
                        {/* Attachment thumbnails — first user message only */}
                        {isFirstUser ? (
                          <div className="flex flex-wrap gap-1.5 justify-end">
                            {seedAttachments.map((att) => {
                              const isImg = att.type === 'image';
                              const isAudio = att.type === 'audio';
                              const isVideo = att.type === 'video';
                              return (
                                <button
                                  key={att.id}
                                  type="button"
                                  title={att.filename || att.type}
                                  onClick={() => isImg && att.storage_url ? setLightboxUrl(att.storage_url) : undefined}
                                  className={`relative rounded-xl overflow-hidden border border-white/15 bg-white/5 flex items-center justify-center transition-opacity hover:opacity-90 ${isImg ? 'cursor-zoom-in w-16 h-16' : 'cursor-default px-2 py-1.5 gap-1.5 h-8'}`}
                                >
                                  {isImg && att.storage_url ? (
                                    <img
                                      src={att.storage_url}
                                      alt={att.filename || 'image'}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <span className="text-[10px] text-[#9CA3AF] flex items-center gap-1">
                                      {isAudio ? '🎙' : isVideo ? '🎬' : '📄'}
                                      <span className="max-w-[80px] truncate">{att.filename || att.type}</span>
                                    </span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        ) : null}
                        <div
                          className={`rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                            m.role === 'user'
                              ? 'bg-[#2DD4BF]/15 border border-[#2DD4BF]/25 text-white'
                              : 'bg-white/[0.04] border border-white/10 text-[#E5E7EB]'
                          }`}
                        >
                          {stripMd(m.content)}
                        </div>
                      </div>
                    </div>
                  );
                })}

                {songRunning && !executed ? (
                  <div
                    className="ml-9 rounded-2xl border border-[#A78BFA]/30 bg-[#A78BFA]/[0.06] p-3.5 flex items-start gap-3 max-w-md"
                    data-testid="create-plan-song-running"
                  >
                    <Loader2 className="w-4 h-4 animate-spin text-[#C4B5FD] shrink-0 mt-0.5" />
                    <div className="min-w-0 space-y-0.5">
                      <p className="text-xs font-semibold text-white">Composing your song…</p>
                      {songGeneration?.prompt ? (
                        <p className="text-[11px] text-[#9CA3AF] line-clamp-2">{songGeneration.prompt}</p>
                      ) : null}
                      <p className="text-[10px] text-[#6B7280]">Usually 1–3 minutes. You can keep this window open.</p>
                    </div>
                  </div>
                ) : null}

                {unifiedStory && !executed ? (
                  <StoryChatQuestionnaire
                    plan={plan}
                    onOpenBuilder={openCharacterBuilder}
                    onPlan={(next) => {
                      if (next?.story_workflow?.state === 'format_review') announce(next);
                      else setPlan(next);
                    }}
                    onScript={(_script, names) => {
                      const cast = names.length ? names.join(', ') : 'none yet';
                      pushAssistant(`The script is ready in the popup. Characters in it: ${cast}. After you close it, build each one — create an AI portrait or upload a photo.`);
                    }}
                  />
                ) : null}

                {unifiedStory && !['format_review', 'format_locked', 'character_review'].includes(plan?.story_workflow?.state) && (question?.field === 'structured_screenplay' || plan?.story_workflow?.structured_screenplay?.scenes?.length) ? (
                  <div className="pl-9 max-w-lg">
                    <StructuredScreenplayReview
                      screenplay={plan?.story_workflow?.structured_screenplay}
                      staleNotice={plan?.story_workflow?.screenplay_stale_notice}
                      reapprovalRequired={Boolean(plan?.story_workflow?.screenplay_reapproval_required)}
                      busy={busy}
                      onGenerate={() => runScreenplay((id) => writeStructuredScreenplay(id), 'Could not write the screenplay')}
                      onEditLine={(lineId, text) => runScreenplay((id) => editStructuredScreenplay(id, { op: 'line', line_id: lineId, text }), 'Could not edit that line')}
                      onEditCue={(cueId, label) => runScreenplay((id) => editStructuredScreenplay(id, { op: 'cue', cue_id: cueId, label }), 'Could not edit that cue')}
                      onEditScene={(body) => runScreenplay((id) => editStructuredScreenplay(id, { op: 'scene', ...body }), 'Could not edit that scene')}
                      onInstruction={(instruction) => runScreenplay((id) => editStructuredScreenplay(id, { op: 'instruction', instruction }), 'Could not update those scenes')}
                      onSpeaker={(name, decision) => runScreenplay((id) => editStructuredScreenplay(id, { op: 'speaker', name, decision }), 'Could not update that speaker')}
                      onLengthen={() => runScreenplay((id) => editStructuredScreenplay(id, { op: 'lengthen' }), 'Could not make the screenplay longer')}
                      onApprove={(screenplayId) => runScreenplay((id) => approveStructuredScreenplay(id, screenplayId), 'Could not approve the screenplay')}
                      speakers={screenplayVoiceRows(plan)}
                      onChangeVoices={() => setCastDialog({ kind: 'voices' })}
                    />
                  </div>
                ) : null}
                {question && question.field !== 'structured_screenplay' && !busy && !executed && !songRunning ? (
                  <div className="space-y-2 pl-9" data-testid="create-plan-question">
                    {question.field === 'story_format' && !unifiedStory ? (
                      <StoryFormatReview
                        key={`${question.format_id || ''}:${question.status || ''}:${question.recommendation?.format || ''}`}
                        storyFormat={{
                          format_id: question.format_id,
                          status: question.status,
                          recommendation: question.recommendation,
                          unknown: question.unknown,
                          fallback_notice: question.fallback_notice,
                          built_from_context_version_id: plan?.story_workflow?.story_format?.built_from_context_version_id,
                        }}
                        storytellingOptions={plan?.field_questions?.['flow_params.story_format']?.options}
                        staleNotice={plan?.story_workflow?.format_stale_notice}
                        onConfirm={(recommendation) => saveStoryFormat(recommendation, 'confirm')}
                        onEdit={(recommendation) => saveStoryFormat(recommendation, 'edit')}
                      />
                    ) : null}
                    {question.field !== 'story_format' && (question.field === 'flow_id' || question.field === 'flow_params.story_format') && (question.options || []).some((o) => o.sample) ? (
                      <div className="space-y-2 max-w-lg" data-testid="create-plan-format-samples">
                        <p className="text-[11px] text-[#9CA3AF]">
                          The suggested format is marked. Compare a short sample scene, then pick one.
                        </p>
                        {(question.options || []).filter((o) => o.sample && !o.disabled).map(renderFormatSampleCard)}
                        <div className="flex flex-wrap gap-2">
                          {(question.options || []).filter((o) => !o.sample && !o.disabled).map(renderOptionChip)}
                        </div>
                      </div>
                    ) : null}
                    {question.options?.length && !((question.field === 'flow_id' || question.field === 'flow_params.story_format') && (question.options || []).some((o) => o.sample)) ? (
                      <div className="flex flex-wrap gap-2">
                        {/* visual.style: while auto-analyzing attached images, show a spinner */}
                        {(question.field === 'visual.style' || question.field === 'visual_style') && styleUploading && !detectedStyleNote ? (
                          <div className="pl-2 pr-3 py-1 rounded-full text-xs border border-[#2DD4BF]/30 bg-[#2DD4BF]/5 text-[#2DD4BF] inline-flex items-center gap-1.5">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            Detecting style from your images…
                          </div>
                        ) : null}
                        {/* visual.style: "Use detected style" replaces normal chips when style known */}
                        {(question.field === 'visual.style' || question.field === 'visual_style') && detectedStyleNote ? (
                          <>
                            {/* Prominent "Use detected style" chip as SUGGESTED */}
                            <button
                              type="button"
                              onClick={() => answerQuestion(detectedStyleNote, 'Style from reference images')}
                              className="pl-1 pr-3 py-1 rounded-full text-xs cursor-pointer border border-[#2DD4BF]/70 bg-[#2DD4BF]/15 text-[#2DD4BF] inline-flex items-center gap-1.5"
                              title={detectedStyleNote}
                            >
                              <span className="w-7 h-7 rounded-full bg-[#2DD4BF]/20 flex items-center justify-center shrink-0">
                                <Sparkles className="w-3.5 h-3.5" />
                              </span>
                              <span>
                                Use detected style
                                <span className="ml-1.5 text-[9px] uppercase tracking-wide opacity-80">suggested</span>
                              </span>
                            </button>
                            {/* Still show other options */}
                            {inlineOptions.map(renderOptionChip)}
                            {hiddenOptionCount ? (
                              <button
                                type="button"
                                onClick={() => setOptionsPopupOpen(true)}
                                data-testid="create-plan-options-more"
                                className="px-3 py-1.5 rounded-full text-xs cursor-pointer border border-white/20 text-[#E5E7EB] hover:border-[#2DD4BF]/50 hover:text-white inline-flex items-center gap-1"
                              >
                                <LayoutGrid className="w-3 h-3" /> More · +{hiddenOptionCount}
                              </button>
                            ) : null}
                          </>
                        ) : (
                          <>
                            {inlineOptions.map(renderOptionChip)}
                            {hiddenOptionCount ? (
                              <button
                                type="button"
                                onClick={() => setOptionsPopupOpen(true)}
                                data-testid="create-plan-options-more"
                                className="px-3 py-1.5 rounded-full text-xs cursor-pointer border border-white/20 text-[#E5E7EB] hover:border-[#2DD4BF]/50 hover:text-white inline-flex items-center gap-1"
                              >
                                <LayoutGrid className="w-3 h-3" /> More · +{hiddenOptionCount}
                              </button>
                            ) : null}
                            {/* Upload reference images chip — only when no style detected yet and not analyzing */}
                            {(question.field === 'visual.style' || question.field === 'visual_style') && !styleUploading ? (
                              <button
                                type="button"
                                onClick={() => {
                                  setStyleNoteEditing('');
                                  setStylePopupOpen(true);
                                }}
                                className="pl-1.5 pr-3 py-1 rounded-full text-xs cursor-pointer border border-dashed border-[#93C5FD]/40 text-[#93C5FD]/80 hover:border-[#93C5FD]/70 hover:text-[#93C5FD] inline-flex items-center gap-1.5 transition-colors"
                                title="Upload reference images to detect visual style"
                              >
                                <Image className="w-3.5 h-3.5" />
                                Upload reference images
                              </button>
                            ) : null}
                          </>
                        )}
                        {otherAllowed ? (
                          <button
                            type="button"
                            aria-pressed={otherOpen}
                            onClick={() => setOtherOpen((open) => !open)}
                            data-testid="create-plan-option-other"
                            className={`px-3 py-1.5 rounded-full text-xs cursor-pointer border border-dashed transition-colors flex items-center gap-1 ${
                              otherOpen
                                ? 'border-[#2DD4BF]/70 bg-[#2DD4BF]/15 text-[#2DD4BF]'
                                : 'border-white/20 text-[#E5E7EB] hover:border-[#2DD4BF]/50 hover:text-white'
                            }`}
                          >
                            <PenLine className="w-3 h-3" /> Other
                          </button>
                        ) : null}
                      </div>
                    ) : null}
                    {/* Detected style preview strip (when style was already set from seed/images) */}
                    {(question.field === 'visual.style' || question.field === 'visual_style') && detectedStyleNote ? (
                      <div className="flex items-start gap-1.5 rounded-xl border border-[#2DD4BF]/20 bg-[#2DD4BF]/5 px-2.5 py-1.5 text-[11px] text-[#2DD4BF]">
                        <Sparkles className="w-3 h-3 mt-0.5 shrink-0" />
                        <span className="line-clamp-2 flex-1" title={detectedStyleNote}>
                          {detectedStyleNote.slice(0, 160)}{detectedStyleNote.length > 160 ? '…' : ''}
                        </span>
                        <button
                          type="button"
                          className="text-[10px] underline opacity-70 hover:opacity-100 shrink-0"
                          onClick={() => { setStyleNoteEditing(detectedStyleNote); setStylePopupOpen(true); }}
                        >
                          Edit
                        </button>
                      </div>
                    ) : null}
                    {otherAllowed && otherOpen ? (
                      <input
                        autoFocus
                        type="text"
                        value={otherText}
                        onChange={(e) => setOtherText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            submitChoiceAnswer();
                          }
                        }}
                        placeholder={
                          question.placeholder || (multiChoice ? 'Type your own — separate several with commas' : 'Type your own')
                        }
                        data-testid="create-plan-other-input"
                        className="w-full max-w-sm px-3 py-1.5 rounded-xl text-xs bg-white/[0.04] border border-white/15 text-white placeholder:text-[#6B7280] focus:outline-none focus:border-[#2DD4BF]/60"
                      />
                    ) : null}
                    {multiChoice || (otherAllowed && otherOpen) ? (
                      <button
                        type="button"
                        onClick={submitChoiceAnswer}
                        disabled={multiChoice ? !multiAnswerCount : !otherText.trim()}
                        data-testid="create-plan-choice-submit"
                        className="px-3.5 py-1.5 rounded-full text-xs font-semibold cursor-pointer bg-[#2DD4BF] text-[#06080D] flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Check className="w-3.5 h-3.5" />
                        {multiChoice && multiAnswerCount > 1 ? `Use these ${multiAnswerCount}` : 'Continue'}
                      </button>
                    ) : null}
                    {question.type === 'text' && question.suggestion ? (
                      <button
                        type="button"
                        onClick={() => answerQuestion(question.suggestion, String(question.suggestion))}
                        className="px-3 py-1.5 rounded-full text-xs cursor-pointer border border-[#2DD4BF]/70 bg-[#2DD4BF]/15 text-[#2DD4BF] max-w-full truncate"
                      >
                        Use “{String(question.suggestion).slice(0, 80)}”
                      </button>
                    ) : null}
                    {question.type === 'text' && question.ai_generate?.kind === 'script' ? (
                      <div className="space-y-2.5 max-w-md w-full">
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => submitScriptGeneration('')}
                            data-testid="create-plan-generate-script"
                            className="px-3.5 py-2 rounded-full text-xs font-semibold cursor-pointer border border-[#A78BFA]/60 bg-[#A78BFA]/10 text-[#C4B5FD] hover:bg-[#A78BFA]/20 flex items-center gap-1.5 disabled:opacity-50"
                          >
                            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                            {String(plan?.input?.script || '').trim()
                              ? 'Regenerate script'
                              : question.ai_generate.label || 'Generate script using AI'}
                          </button>
                          <button
                            type="button"
                            disabled={busy}
                            aria-expanded={scriptFormOpen}
                            onClick={() => {
                              setScriptFormOpen((open) => !open);
                              if (!scriptPrompt && question.ai_generate?.default_prompt) {
                                setScriptPrompt('');
                              }
                            }}
                            data-testid="create-plan-script-prompt-toggle"
                            className={`px-3.5 py-2 rounded-full text-xs font-semibold cursor-pointer border flex items-center gap-1.5 disabled:opacity-50 transition-colors ${
                              scriptFormOpen
                                ? 'border-[#A78BFA] bg-[#A78BFA]/20 text-[#DDD6FE]'
                                : 'border-white/20 text-[#E5E7EB] hover:border-[#A78BFA]/50'
                            }`}
                          >
                            <PenLine className="w-3.5 h-3.5" />
                            {String(plan?.input?.script || '').trim()
                              ? 'Change with new prompt'
                              : 'Add direction'}
                          </button>
                        </div>
                        {scriptFormOpen ? (
                          <div
                            className="rounded-2xl border border-[#A78BFA]/30 bg-[#A78BFA]/[0.06] p-3 space-y-2.5"
                            data-testid="create-plan-script-form"
                          >
                            <label className="block space-y-1">
                              <span className="text-[11px] font-semibold text-[#DDD6FE]">
                                {String(plan?.input?.script || '').trim()
                                  ? 'How should the script change?'
                                  : 'Any direction for the script?'}
                              </span>
                              <textarea
                                autoFocus
                                rows={3}
                                maxLength={2000}
                                value={scriptPrompt}
                                onChange={(e) => setScriptPrompt(e.target.value)}
                                placeholder={
                                  String(plan?.input?.script || '').trim()
                                    ? 'e.g. Make it funnier, shorter, and add a punchy ending'
                                    : 'e.g. Keep it punchy, compare men vs women in a light roast style'
                                }
                                className="w-full px-3 py-2 rounded-xl text-xs bg-black/30 border border-white/15 text-white placeholder:text-[#6B7280] focus:outline-none focus:border-[#A78BFA]/70 resize-y"
                              />
                            </label>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => submitScriptGeneration()}
                                disabled={busy || !scriptPrompt.trim()}
                                data-testid="create-plan-script-submit"
                                className="px-3.5 py-1.5 rounded-full text-xs font-semibold cursor-pointer bg-[#A78BFA] text-[#0B0718] flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                              >
                                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                                {String(plan?.input?.script || '').trim() ? 'Regenerate' : 'Generate'}
                              </button>
                              <button
                                type="button"
                                onClick={() => setScriptFormOpen(false)}
                                className="px-3 py-1.5 rounded-full text-xs cursor-pointer text-[#9CA3AF] hover:text-white"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                    {question.type === 'voice' ? (
                      <VoicePickerQuestion
                        planId={plan.id}
                        language={plan.input?.language || ''}
                        prompt={question.prompt}
                        onPlan={setPlan}
                        onPick={(value, label) => answerQuestion(value, label)}
                        onAuto={() => answerQuestion({ provider: 'auto' }, 'Let Twinnee pick a voice')}
                      />
                    ) : null}
                    {question.type === 'upload' ? (
                      <>
                        <input
                          ref={fileInputRef}
                          type="file"
                          className="hidden"
                          multiple={Boolean(question.multiple)}
                          accept={ATTACH_ACCEPT[question.accept] || undefined}
                          onChange={handleUpload}
                        />
                        {question.capture ? (
                          <input
                            ref={cameraInputRef}
                            type="file"
                            className="hidden"
                            accept={ATTACH_ACCEPT[question.accept] || 'image/*'}
                            capture={question.capture === true ? 'user' : question.capture}
                            onChange={handleUpload}
                          />
                        ) : null}
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            disabled={uploadBusy}
                            onClick={() => fileInputRef.current?.click()}
                            className="px-3.5 py-2 rounded-full text-xs font-semibold cursor-pointer border border-[#2DD4BF]/60 bg-[#2DD4BF]/10 text-[#2DD4BF] flex items-center gap-1.5 disabled:opacity-50"
                          >
                            {uploadBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                            {uploadBusy ? 'Uploading…' : `Upload ${fieldLabel(question.field).toLowerCase()}`}
                          </button>
                          {question.capture ? (
                            <button
                              type="button"
                              disabled={uploadBusy}
                              onClick={() => cameraInputRef.current?.click()}
                              className="px-3.5 py-2 rounded-full text-xs font-semibold cursor-pointer border border-[#2DD4BF]/60 bg-[#2DD4BF]/10 text-[#2DD4BF] flex items-center gap-1.5 disabled:opacity-50"
                            >
                              {uploadBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
                              Take photo
                            </button>
                          ) : null}
                          {question.ai_generate ? (
                            <button
                              type="button"
                              disabled={uploadBusy}
                              aria-expanded={songFormOpen}
                              onClick={() => setSongFormOpen((isOpen) => !isOpen)}
                              data-testid="create-plan-ai-generate"
                              className={`px-3.5 py-2 rounded-full text-xs font-semibold cursor-pointer border flex items-center gap-1.5 disabled:opacity-50 transition-colors ${
                                songFormOpen
                                  ? 'border-[#A78BFA] bg-[#A78BFA]/20 text-[#DDD6FE]'
                                  : 'border-[#A78BFA]/60 bg-[#A78BFA]/10 text-[#C4B5FD] hover:bg-[#A78BFA]/20'
                              }`}
                            >
                              <Wand2 className="w-3.5 h-3.5" /> {question.ai_generate.label || 'Generate using AI'}
                            </button>
                          ) : null}
                        </div>
                        {question.ai_generate && songFormOpen ? (
                          <div
                            className="rounded-2xl border border-[#A78BFA]/30 bg-[#A78BFA]/[0.06] p-3 space-y-2.5 max-w-md"
                            data-testid="create-plan-song-form"
                          >
                            <label className="block space-y-1">
                              <span className="text-[11px] font-semibold text-[#DDD6FE]">Describe the song</span>
                              <textarea
                                autoFocus
                                rows={3}
                                maxLength={2000}
                                value={songPrompt}
                                onChange={(e) => setSongPrompt(e.target.value)}
                                placeholder="Style, mood and what it's about — e.g. upbeat Hindi pop song about a fox who wants to reach the moon"
                                className="w-full px-3 py-2 rounded-xl text-xs bg-black/30 border border-white/15 text-white placeholder:text-[#6B7280] focus:outline-none focus:border-[#A78BFA]/70 resize-y"
                              />
                            </label>
                            <label className="flex items-center gap-2 text-[11px] text-[#E5E7EB] cursor-pointer select-none">
                              <input
                                type="checkbox"
                                checked={songInstrumental}
                                onChange={(e) => setSongInstrumental(e.target.checked)}
                                className="accent-[#A78BFA]"
                              />
                              Instrumental only (no vocals)
                            </label>
                            {!songInstrumental ? (
                              songLyricsOpen ? (
                                <label className="block space-y-1">
                                  <span className="text-[11px] font-semibold text-[#DDD6FE]">
                                    Lyrics <span className="font-normal text-[#9CA3AF]">— optional, AI writes them if empty</span>
                                  </span>
                                  <textarea
                                    rows={5}
                                    maxLength={3500}
                                    value={songLyrics}
                                    onChange={(e) => setSongLyrics(e.target.value)}
                                    placeholder={'[Verse]\n…\n[Chorus]\n…'}
                                    className="w-full px-3 py-2 rounded-xl text-xs font-mono bg-black/30 border border-white/15 text-white placeholder:text-[#6B7280] focus:outline-none focus:border-[#A78BFA]/70 resize-y"
                                  />
                                </label>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => setSongLyricsOpen(true)}
                                  className="text-[11px] text-[#C4B5FD] hover:text-white underline-offset-2 hover:underline cursor-pointer flex items-center gap-1"
                                >
                                  <PenLine className="w-3 h-3" /> Write my own lyrics
                                </button>
                              )
                            ) : null}
                            <div className="flex items-center gap-2 pt-0.5">
                              <button
                                type="button"
                                onClick={submitSongGeneration}
                                disabled={!songPrompt.trim() && (songInstrumental || !songLyrics.trim())}
                                data-testid="create-plan-song-submit"
                                className="px-3.5 py-1.5 rounded-full text-xs font-semibold cursor-pointer bg-[#A78BFA] text-[#0B0718] flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                              >
                                <TwinneeLogo className="w-3.5 h-3.5" /> Compose song
                              </button>
                              <button
                                type="button"
                                onClick={() => setSongFormOpen(false)}
                                className="px-3 py-1.5 rounded-full text-xs cursor-pointer text-[#9CA3AF] hover:text-white"
                              >
                                Cancel
                              </button>
                              <span className="ml-auto text-[10px] text-[#9CA3AF]">Takes 1–3 min · uses credits</span>
                            </div>
                          </div>
                        ) : null}
                      </>
                    ) : null}
                    {!question.required ? (
                      <button
                        type="button"
                        onClick={skipQuestion}
                        className="px-3 py-1.5 rounded-full text-xs cursor-pointer border border-dashed border-white/20 text-[#9CA3AF] hover:text-white hover:border-white/40"
                        data-testid="create-plan-skip"
                      >
                        {question.skip_label || (question.default_label ? `Skip — use ${question.default_label}` : 'Skip')}
                      </button>
                    ) : null}
                    {hasSuggestionsForPending && question.field !== 'flow_id' ? (
                      <button
                        type="button"
                        onClick={() => runAnswer({ acceptSuggestions: true }, 'Use your suggestions for the rest')}
                        className="text-[11px] text-[#9CA3AF] hover:text-white underline-offset-2 hover:underline cursor-pointer flex items-center gap-1"
                      >
                        <Wand2 className="w-3 h-3" /> Use suggested / default values for all remaining questions
                      </button>
                    ) : null}
                  </div>
                ) : null}

                {plan && !executed && String(plan?.input?.script || '').trim() && question?.field !== 'script' ? (
                  <div
                    className="ml-9 rounded-2xl border border-[#A78BFA]/25 bg-[#A78BFA]/[0.05] p-3 space-y-2 max-w-md"
                    data-testid="create-plan-script-revise"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-[11px] text-[#C4B5FD] font-medium flex items-center gap-1.5 min-w-0 flex-1">
                        <BookOpen className="w-3.5 h-3.5 shrink-0" />
                        Script ready — change or regenerate anytime
                      </p>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => setEditingField('script')}
                        className="px-2.5 py-1 rounded-full text-[11px] cursor-pointer border border-white/15 text-[#E5E7EB] hover:border-white/30 disabled:opacity-50"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => submitScriptGeneration('')}
                        className="px-2.5 py-1 rounded-full text-[11px] cursor-pointer border border-[#A78BFA]/50 bg-[#A78BFA]/10 text-[#C4B5FD] hover:bg-[#A78BFA]/20 flex items-center gap-1 disabled:opacity-50"
                      >
                        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                        Regenerate
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        aria-expanded={scriptFormOpen}
                        onClick={() => setScriptFormOpen((open) => !open)}
                        data-testid="create-plan-script-revise-toggle"
                        className={`px-2.5 py-1 rounded-full text-[11px] cursor-pointer border flex items-center gap-1 disabled:opacity-50 ${
                          scriptFormOpen
                            ? 'border-[#A78BFA] bg-[#A78BFA]/20 text-[#DDD6FE]'
                            : 'border-white/15 text-[#E5E7EB] hover:border-[#A78BFA]/40'
                        }`}
                      >
                        <PenLine className="w-3 h-3" /> New prompt
                      </button>
                    </div>
                    {scriptFormOpen ? (
                      <div className="space-y-2">
                        <textarea
                          autoFocus
                          rows={2}
                          maxLength={2000}
                          value={scriptPrompt}
                          onChange={(e) => setScriptPrompt(e.target.value)}
                          placeholder="e.g. Make it funnier and cut 10 seconds"
                          className="w-full px-3 py-2 rounded-xl text-xs bg-black/30 border border-white/15 text-white placeholder:text-[#6B7280] focus:outline-none focus:border-[#A78BFA]/70 resize-y"
                        />
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            disabled={busy || !scriptPrompt.trim()}
                            onClick={() => submitScriptGeneration()}
                            data-testid="create-plan-script-revise-submit"
                            className="px-3 py-1.5 rounded-full text-[11px] font-semibold cursor-pointer bg-[#A78BFA] text-[#0B0718] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
                          >
                            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
                            Regenerate with prompt
                          </button>
                          <button
                            type="button"
                            onClick={() => setScriptFormOpen(false)}
                            className="text-[11px] text-[#9CA3AF] hover:text-white cursor-pointer"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {execution?.mode === 'api' || execution?.mode === 'handoff' ? (
                  <div className="rounded-2xl border border-[#2DD4BF]/25 bg-[#2DD4BF]/[0.06] p-3.5 space-y-2.5 ml-9">
                    {execution.mode === 'api' && execution.job_id ? (
                      <>
                        <div className="flex items-center justify-between gap-2 text-xs text-white">
                          <span className="truncate">
                            {progressLabel
                              || (jobStopped
                              ? `${job.status === 'cancelled' ? 'Cancelled' : 'Failed'} at ${jobProgress}%`
                              : jobAwaitingReview
                                ? (job?.message || 'Ready for your review')
                                : job?.message || 'Queued…')}
                          </span>
                          <span
                            className={`font-mono ${
                              visualStats.incomplete
                                ? 'text-[#FBBF24]'
                                : jobStopped ? 'text-[#F87171]' : jobAwaitingReview ? 'text-[#FBBF24]' : 'text-[#2DD4BF]'
                            }`}
                          >
                            {jobProgress}%
                          </span>
                        </div>
                        <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                          <div
                            className={`h-full transition-all ${
                              visualStats.incomplete
                                ? 'bg-[#FBBF24]'
                                : jobStopped ? 'bg-[#F87171]' : jobAwaitingReview ? 'bg-[#FBBF24]' : 'bg-[#2DD4BF]'
                            }`}
                            style={{ width: `${jobProgress}%` }}
                          />
                        </div>
                        {sfxStepLabel(outputs?.sound_design) ? (
                          <p className="text-[10px] text-[#5EEAD4]" data-testid="create-plan-sfx-step">
                            {sfxStepLabel(outputs?.sound_design)}
                          </p>
                        ) : null}
                        {jobAwaitingReview ? (
                          <p className="text-[11px] leading-snug text-[#FDE68A]">
                            {usesReels(execution)
                              ? 'Waiting on you — review transcripts, then run the AI editor (not stuck).'
                              : 'Waiting on you — open the project to continue (not stuck).'}
                          </p>
                        ) : null}
                        {jobStopped ? (
                          <p className="text-[11px] leading-snug text-[#FCA5A5] break-words">{jobFailureReason(job)}</p>
                        ) : null}
                        {jobStopped && job?.failed_step ? (
                          <p className="text-[10px] text-[#9CA3AF] truncate">Stopped at: {job.failed_step}</p>
                        ) : null}
                        {/* Inline pipeline step buttons — enabled by REACT_APP_PIPELINE_STEPS_IN_CHAT=true */}
                        {PIPELINE_STEPS_IN_CHAT && isScriptBasedExecution && sbNextSteps.length > 0 ? (
                          <div className="space-y-1.5 pt-0.5" data-testid="pipeline-steps-inline">
                            {sbNextSteps.map((step) => (
                              <div
                                key={step.id}
                                className={`flex items-center justify-between gap-2 rounded-xl px-3 py-2 text-xs ${
                                  step.done
                                    ? 'bg-[#2DD4BF]/10 border border-[#2DD4BF]/20'
                                    : step.current
                                      ? 'bg-white/[0.05] border border-white/20'
                                      : 'bg-white/[0.02] border border-white/10 opacity-50'
                                }`}
                              >
                                <div className="flex items-center gap-2 min-w-0">
                                  {step.done ? (
                                    <CheckCircle className="w-3.5 h-3.5 text-[#2DD4BF] shrink-0" />
                                  ) : step.current ? (
                                    <Loader2 className={`w-3.5 h-3.5 shrink-0 text-[#7DD3FC] ${((sbJobRunning && !(step.id === 'prompts' && sbPromptsStuck)) || pipelinePhaseLoading) ? 'animate-spin' : ''}`} />
                                  ) : (
                                    <Circle className="w-3.5 h-3.5 shrink-0 text-[#6B7280]" />
                                  )}
                                  <span className={step.done ? 'text-[#5EEAD4]' : step.current ? 'text-white' : 'text-[#6B7280]'}>
                                    {step.label}
                                  </span>
                                </div>
                                {step.id === 'prompts' && step.current && !step.done && !sbIsWebScraper ? (
                                  <button
                                    type="button"
                                    disabled={Boolean(pipelinePhaseLoading) || (sbJobRunning && !sbPromptsStuck)}
                                    onClick={sbGeneratePrompts}
                                    className="shrink-0 px-2.5 py-1 rounded-lg bg-violet-500/20 border border-violet-400/30 text-violet-300 text-[11px] font-semibold flex items-center gap-1 disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed hover:bg-violet-500/30"
                                  >
                                    {pipelinePhaseLoading === 'prompts' ? (
                                      <Loader2 className="w-3 h-3 animate-spin" />
                                    ) : (
                                      <Wand2 className="w-3 h-3" />
                                    )}
                                    Generate prompts
                                  </button>
                                ) : null}
                                {step.id === 'images' && step.current && !step.done ? (
                                  <button
                                    type="button"
                                    disabled={Boolean(pipelinePhaseLoading) || sbJobRunning || sbImagesInProgress}
                                    onClick={sbGenerateImages}
                                    className="shrink-0 px-2.5 py-1 rounded-lg bg-[#2DD4BF]/20 border border-[#2DD4BF]/30 text-[#5EEAD4] text-[11px] font-semibold flex items-center gap-1 disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed hover:bg-[#2DD4BF]/30"
                                  >
                                    {pipelinePhaseLoading === 'images' || sbImagesInProgress ? (
                                      <Loader2 className="w-3 h-3 animate-spin" />
                                    ) : (
                                      <ImageIcon className="w-3 h-3" />
                                    )}
                                    {pipelinePhaseLoading === 'images' || sbImagesInProgress
                                      ? 'Working…'
                                      : sbIsWebScraper ? 'Fetch images' : 'Generate images'}
                                  </button>
                                ) : null}
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </>
                    ) : null}
                    {jobAwaitingReview && usesReels(execution) && reelsProjectId ? (
                      <div className="grid grid-cols-2 gap-1.5" data-testid="create-plan-reels-review-actions">
                        <button
                          type="button"
                          onClick={() => void runReelsAudioPolish({ auto: false })}
                          disabled={reelsBusy === 'clean' || reelsBusy === 'retranscribe'}
                          className="py-2 rounded-xl border border-[#FBBF24]/40 bg-[#FBBF24]/10 text-[#FDE68A] text-[11px] font-semibold cursor-pointer hover:bg-[#FBBF24]/20 disabled:opacity-50"
                        >
                          {reelsBusy === 'clean' || reelsBusy === 'retranscribe' ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1" />
                          ) : null}
                          Detect & clean
                        </button>
                        <button
                          type="button"
                          onClick={() => setReelsDialog({ kind: 'transcripts' })}
                          className="py-2 rounded-xl border border-white/15 bg-white/[0.04] text-[#E5E7EB] text-[11px] font-semibold cursor-pointer hover:bg-white/[0.08]"
                        >
                          Review subtitles
                        </button>
                        <button
                          type="button"
                          onClick={() => setReelsDialog({ kind: 'placement' })}
                          className="py-2 rounded-xl border border-white/15 bg-white/[0.04] text-[#E5E7EB] text-[11px] font-semibold cursor-pointer hover:bg-white/[0.08]"
                        >
                          Title placement
                        </button>
                        <button
                          type="button"
                          onClick={runReelsAiEditor}
                          disabled={reelsBusy === 'editor'}
                          className="py-2 rounded-xl bg-rose-500 hover:bg-rose-400 text-black text-[11px] font-bold cursor-pointer disabled:opacity-50 flex items-center justify-center gap-1"
                        >
                          {reelsBusy === 'editor' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                          Generate edit
                        </button>
                      </div>
                    ) : null}
                    {canRestartDialogueStory(execution) ? (
                      <>
                        <button
                          type="button"
                          onClick={() => handleRetry({ continueFrom: true })}
                          disabled={retrying}
                          data-testid="create-plan-continue"
                          className="w-full py-2 rounded-xl border border-[#2DD4BF]/50 bg-[#2DD4BF]/10 text-[#5EEAD4] text-xs font-bold cursor-pointer flex items-center justify-center gap-1.5 hover:bg-[#2DD4BF]/20 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {retrying && retryAction === 'continue' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                          {retrying && retryAction === 'continue' ? 'Continuing…' : 'Continue from where left'}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRetry({ withLooks: true })}
                          disabled={retrying}
                          data-testid="create-plan-retry"
                          className="w-full py-2 rounded-xl border border-[#F87171]/50 bg-[#F87171]/10 text-[#FECACA] text-xs font-bold cursor-pointer flex items-center justify-center gap-1.5 hover:bg-[#F87171]/20 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {retrying && retryAction === 'regenerate' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                          {retrying && retryAction === 'regenerate' ? 'Regenerating…' : 'Regenerate whole video'}
                        </button>
                      </>
                    ) : canRetryExecution(execution, job) ? (
                      <button
                        type="button"
                        onClick={() => handleRetry()}
                        disabled={retrying}
                        data-testid="create-plan-retry"
                        className="w-full py-2 rounded-xl border border-[#F87171]/50 bg-[#F87171]/10 text-[#FECACA] text-xs font-bold cursor-pointer flex items-center justify-center gap-1.5 hover:bg-[#F87171]/20 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {retrying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                        {retrying ? 'Retrying…' : 'Retry'}
                      </button>
                    ) : null}
                    {outputs?.can_convert_images_to_videos && (Number(outputs.pending_ai_videos) || 0) > 0 && job?.status === 'completed' ? (
                      <button
                        type="button"
                        onClick={() => void handleConvertImagesToVideos()}
                        disabled={convertingImages || rerendering}
                        data-testid="create-plan-convert-videos"
                        className="w-full py-2 rounded-xl border border-[#2DD4BF]/50 bg-[#2DD4BF]/10 text-[#5EEAD4] text-xs font-bold cursor-pointer flex items-center justify-center gap-1.5 hover:bg-[#2DD4BF]/20 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {convertingImages ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Clapperboard className="w-3.5 h-3.5" />}
                        {convertingImages ? 'Converting…' : 'Convert images to AI videos'}
                      </button>
                    ) : null}
                    {execution.mode === 'api' && (finalVideoUrl || job?.status === 'completed' || sbReadyForScenes) ? (
                      <div className="space-y-1.5" data-testid="create-plan-export-actions">
                        <div className="grid grid-cols-3 gap-1.5">
                          <button
                            type="button"
                            disabled={!finalVideoUrl}
                            onClick={() => downloadVideoFile(finalVideoUrl, videoFilename(outputs?.title || plan?.input?.title))}
                            className="py-2 rounded-xl border border-white/15 bg-white/[0.04] text-[#E5E7EB] text-[11px] font-semibold cursor-pointer flex items-center justify-center gap-1 hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid="create-plan-export-download"
                            title={finalVideoUrl ? 'Download MP4' : 'Export unlocks after you save an MP4 from the editor'}
                          >
                            <Download className="w-3.5 h-3.5" /> Export
                          </button>
                          {PUBLISH_PLATFORMS.map(({ id, label, Icon, accent }) => (
                            <button
                              key={id}
                              type="button"
                              onClick={() => openPublish(id)}
                              disabled={!finalVideoUrl || publish[id]?.status === 'publishing'}
                              className="py-2 rounded-xl border border-white/15 bg-white/[0.04] text-[#E5E7EB] text-[11px] font-semibold cursor-pointer flex items-center justify-center gap-1 hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
                              data-testid={`create-plan-export-${id}`}
                            >
                              {publish[id]?.status === 'publishing' ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <Icon className={`w-3.5 h-3.5 ${accent}`} />
                              )}
                              {label}
                            </button>
                          ))}
                        </div>
                        {!finalVideoUrl ? (
                          <div className="space-y-1.5 px-0.5">
                            <p className="text-[10px] leading-snug text-[#9CA3AF]">
                              {visualStats.incomplete
                                ? incompleteImagesMessage(visualStats)
                                : outputs?.auto_export?.can_manual_retry
                                  ? 'Auto-compile failed after 3 attempts — retry below.'
                                  : 'Compiling final MP4 automatically — Export unlocks when ready (no Open project needed).'}
                            </p>
                            {outputs?.auto_export?.can_manual_retry && !visualStats.incomplete ? (
                              <button
                                type="button"
                                onClick={() => void handleRetryExport()}
                                disabled={retryingExport}
                                className="w-full py-1.5 rounded-lg border border-[#FBBF24]/40 bg-[#FBBF24]/10 text-[#FDE68A] text-[11px] font-semibold cursor-pointer disabled:opacity-50"
                                data-testid="create-plan-retry-export"
                              >
                                {retryingExport ? 'Starting…' : 'Retry final video compile'}
                              </button>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                    <button
                      type="button"
                      onClick={openProject}
                      className="w-full py-2 rounded-xl bg-gradient-to-r from-[#93C5FD] via-[#7DD3FC] to-[#5EEAD4] text-[#06080D] text-xs font-bold cursor-pointer flex items-center justify-center gap-1"
                    >
                      {openProjectLabel}{' '}
                      <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : null}

                {busy ? (
                  <div className="flex items-center gap-2 text-xs text-[#9CA3AF] pl-9">
                    <Loader2 className="w-3.5 h-3.5 animate-spin text-[#2DD4BF]" />
                    {restoring
                      ? 'Restoring your conversation…'
                      : plan
                        ? 'Updating the plan…'
                        : 'Understanding what you want to create…'}
                  </div>
                ) : null}
                {castBusy ? (
                  <div className="flex items-center gap-2 text-xs text-[#9CA3AF] pl-9" data-testid="create-plan-cast-busy">
                    <Loader2 className="w-3.5 h-3.5 animate-spin text-[#2DD4BF]" />
                    {CAST_BUSY_LABELS[castBusy] || 'Working…'}
                  </div>
                ) : null}
                {courseBusy ? (
                  <div className="flex items-center gap-2 text-xs text-[#9CA3AF] pl-9" data-testid="create-plan-course-busy">
                    <Loader2 className="w-3.5 h-3.5 animate-spin text-[#2DD4BF]" />
                    {courseBusy === 'plan'
                      ? 'Planning course…'
                      : courseBusy === 'align'
                        ? 'Running alignment pipeline…'
                        : 'Starting lesson artifacts…'}
                  </div>
                ) : null}
              </div>

              <div className="shrink-0 border-t border-white/10 p-3 sm:p-4 bg-[#0b0e14] space-y-2">
                {plan?.can_execute && !executed && coursePlanFlow ? (
                  <div className="flex flex-wrap gap-2" data-testid="create-plan-course-actions">
                    {(() => {
                      const primary =
                        'flex-1 py-2.5 rounded-xl bg-gradient-to-r from-[#93C5FD] via-[#7DD3FC] to-[#5EEAD4] text-[#06080D] text-xs font-bold cursor-pointer flex items-center justify-center gap-1.5 disabled:opacity-60';
                      const locked = busy || Boolean(courseBusy);
                      if (currentCourseStage === 'plan') {
                        return (
                          <button
                            type="button"
                            disabled={locked}
                            onClick={() => continueCourse(plan)}
                            className={primary}
                            data-testid="create-plan-course-plan"
                          >
                            <BookOpen className="w-3.5 h-3.5" /> Analyze & plan course
                          </button>
                        );
                      }
                      if (currentCourseStage === 'artifacts' && !course?.explain_job_id) {
                        return (
                          <>
                            <button
                              type="button"
                              disabled={locked}
                              onClick={() =>
                                startLessonArtifacts({
                                  language: course?.language || plan?.input?.language || 'en',
                                  tone: course?.tone || 'serious',
                                })
                              }
                              className={primary}
                              data-testid="create-plan-course-artifacts"
                            >
                              <Wand2 className="w-3.5 h-3.5" /> Generate lesson artifacts
                            </button>
                            <button
                              type="button"
                              disabled={locked}
                              onClick={() => setCourseDialog({ kind: 'narration' })}
                              className="px-3 py-2.5 rounded-xl text-xs font-semibold border border-white/15 text-[#E5E7EB] hover:border-[#2DD4BF]/50 cursor-pointer disabled:opacity-50"
                              data-testid="create-plan-course-artifacts-options"
                              title="Change narration language or style"
                            >
                              Options
                            </button>
                          </>
                        );
                      }
                      if (course?.align_status === 'completed' || currentCourseStage === 'done') {
                        return (
                          <button
                            type="button"
                            disabled={locked}
                            onClick={() => setAlignPopupOpen(true)}
                            className={primary}
                            data-testid="create-plan-course-aligned"
                          >
                            <Play className="w-3.5 h-3.5" /> View alignment result
                          </button>
                        );
                      }
                      if (currentCourseStage === 'align' || course?.explain_status === 'completed') {
                        return (
                          <button
                            type="button"
                            disabled={locked}
                            onClick={() => startCourseAlignment()}
                            className={primary}
                            data-testid="create-plan-course-align"
                          >
                            {courseBusy === 'align' ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <Play className="w-3.5 h-3.5" />
                            )}
                            {course?.align_status === 'failed' ? 'Retry alignment' : 'Run alignment pipeline'}
                          </button>
                        );
                      }
                      return (
                        <button
                          type="button"
                          disabled
                          className={primary}
                          data-testid="create-plan-course-running"
                        >
                          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating artifacts…
                        </button>
                      );
                    })()}
                  </div>
                ) : null}
                {!executed && castPlan && (plan?.can_execute || (unifiedStory && !question && currentCastStage && currentCastStage !== 'done')) ? (
                  <div className="flex flex-wrap gap-2" data-testid="create-plan-cast-actions">
                    {(() => {
                      const primary =
                        'flex-1 py-2.5 rounded-xl bg-gradient-to-r from-[#93C5FD] via-[#7DD3FC] to-[#5EEAD4] text-[#06080D] text-xs font-bold cursor-pointer flex items-center justify-center gap-1.5 disabled:opacity-60';
                      const secondary =
                        'px-3 py-2.5 rounded-xl border border-dashed border-[#2DD4BF]/50 text-[#5EEAD4] text-xs font-semibold hover:bg-[#2DD4BF]/10 cursor-pointer flex items-center gap-1.5 disabled:opacity-60';
                      const locked = busy || Boolean(castBusy);
                      const hasMembers = Boolean(plan.cast?.characters?.length);
                      if (currentCastStage === 'overview') {
                        return (
                          <button
                            type="button"
                            disabled={locked}
                            onClick={() => {
                              pushUser('Write the story overview');
                              continueCast(plan);
                            }}
                            className={primary}
                            data-testid="create-plan-cast-overview"
                          >
                            <PenLine className="w-3.5 h-3.5" /> Write story overview
                          </button>
                        );
                      }
                      if (currentCastStage === 'characters') {
                        return (
                          <>
                            <button
                              type="button"
                              disabled={locked}
                              onClick={() => continueCast(plan)}
                              className={primary}
                              data-testid="create-plan-cast-build"
                            >
                              <Users className="w-3.5 h-3.5" /> {hasMembers ? 'Build characters' : 'Find characters'}
                            </button>
                            {hasMembers ? (
                              <button
                                type="button"
                                disabled={locked}
                                onClick={autoBuildFromChat}
                                className={secondary}
                                data-testid="create-plan-cast-auto-build"
                              >
                                <TwinneeLogo className="w-3.5 h-3.5" /> Let Twinnee build them
                              </button>
                            ) : null}
                          </>
                        );
                      }
                      if (currentCastStage === 'voices') {
                        return (
                          <>
                            <button
                              type="button"
                              disabled={locked}
                              onClick={() => setCastDialog({ kind: 'voices' })}
                              className={primary}
                              data-testid="create-plan-cast-voices"
                            >
                              <Mic className="w-3.5 h-3.5" /> Allocate voices
                            </button>
                            <button
                              type="button"
                              disabled={locked}
                              onClick={autoVoicesFromChat}
                              className={secondary}
                              data-testid="create-plan-cast-auto-voices"
                            >
                              <TwinneeLogo className="w-3.5 h-3.5" /> Auto-assign voices
                            </button>
                          </>
                        );
                      }
                      return (
                        <button
                          type="button"
                          disabled={locked}
                          onClick={() => handleExecute('auto')}
                          className={primary}
                          data-testid="create-plan-execute"
                        >
                          <Play className="w-3.5 h-3.5" /> Generate story
                        </button>
                      );
                    })()}
                  </div>
                ) : null}
                {plan?.flow?.id === 'unified_story' && !executed ? (
                  <div className="flex items-center gap-3">
                    {plan?.story_workflow?.state === 'analyzing_sources' ? (
                      <p className="text-[11px] text-[#A78BFA] flex items-center gap-1.5" data-testid="unified-story-reading">
                        <Loader2 className="w-3 h-3 animate-spin" /> Reading your files…
                      </p>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => setStoryPipelineOpen(true)}
                      className="text-[10px] text-[#6B7280] underline cursor-pointer"
                      data-testid="unified-story-pipeline-open"
                    >
                      Debug details
                    </button>
                  </div>
                ) : null}
                {plan?.can_execute && !executed && !castPlan && !coursePlanFlow ? (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => handleExecute('auto')}
                      className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-[#93C5FD] via-[#7DD3FC] to-[#5EEAD4] text-[#06080D] text-xs font-bold cursor-pointer flex items-center justify-center gap-1.5 disabled:opacity-60"
                      data-testid="create-plan-execute"
                      title={plan.ready ? undefined : 'Remaining optional questions will use their defaults'}
                    >
                      <Play className="w-3.5 h-3.5" /> {plan.ready ? 'Execute plan' : 'Start now with defaults'}
                    </button>
                  </div>
                ) : null}
                <div className="rounded-2xl border border-white/10 bg-black/40 p-2.5 flex items-end gap-2">
                  <textarea
                    rows={2}
                    value={composer}
                    disabled={executed}
                    onChange={(e) => setComposer(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleComposerSend();
                      }
                    }}
                    placeholder={
                      executed
                        ? 'Plan executed'
                        : !plan
                          ? 'Describe what you want to create…'
                          : question && question.type !== 'upload' && question.type !== 'voice'
                          ? question.placeholder || 'Type your answer…'
                          : 'Ask for changes, e.g. "make it vertical and in Hindi"'
                    }
                    className="flex-1 bg-transparent border-none text-base sm:text-sm text-white placeholder:text-[#6B7280] focus:outline-none resize-none min-h-[44px]"
                  />
                  <button
                    type="button"
                    disabled={!composer.trim() || busy || executed}
                    onClick={handleComposerSend}
                    className="w-8 h-8 rounded-full bg-white text-[#06080D] flex items-center justify-center cursor-pointer disabled:opacity-40 shrink-0"
                    aria-label="Send"
                  >
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            <div
              className={`${mobilePane === 'preview' ? 'flex' : 'hidden'} lg:flex lg:col-span-7 relative min-h-0 h-full flex-col bg-[#0c1018] overflow-y-auto`}
            >
              <div className="relative z-10 p-3 sm:p-4 flex items-center gap-2">
                {(showOutputs ? ['output', 'plan', 'json'] : ['plan', 'json']).map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setPreviewTab(tab)}
                    data-testid={`create-plan-tab-${tab}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold cursor-pointer border ${
                      activeTab === tab ? 'bg-white/10 border-white/20 text-white' : 'border-transparent text-[#9CA3AF]'
                    }`}
                  >
                    {tab === 'output' ? 'Output' : tab === 'plan' ? 'Plan' : 'JSON'}
                  </button>
                ))}
                {plan ? (
                  <span className="ml-auto text-[10px] font-mono text-[#9CA3AF]">
                    {plan.ready ? 'ready' : `${pendingCount} pending`} · {plan.contract_version}
                  </span>
                ) : null}
              </div>

              <div className="relative z-10 flex-1 px-4 pb-8 flex justify-center">
                {!plan && busy ? (
                  <div className="self-center flex items-center gap-2 text-sm text-[#9CA3AF]">
                    <Loader2 className="w-4 h-4 animate-spin text-[#2DD4BF]" />
                    {restoring ? 'Loading plan…' : 'Drafting plan…'}
                  </div>
                ) : !plan ? (
                  <div className="self-center text-sm text-[#6B7280] text-center max-w-xs">
                    Describe what you want to create and the plan will appear here.
                  </div>
                ) : activeTab === 'output' ? (
                  <CreatePlanOutputPanel
                    outputs={outputs}
                    loading={outputsLoading}
                    error={outputsError}
                    job={job}
                    publish={publish}
                    onPublish={openPublish}
                    onRefresh={refreshOutputs}
                    onOpenEditor={openEditor}
                    onRerender={handleRerender}
                    rerendering={rerendering}
                    onConvertImagesToVideos={handleConvertImagesToVideos}
                    convertingImages={convertingImages}
                    onRetryExport={handleRetryExport}
                    retryingExport={retryingExport}
                  />
                ) : activeTab === 'json' ? (
                  <pre className="w-full max-w-2xl rounded-2xl border border-white/10 bg-black/50 p-4 text-[11px] leading-relaxed text-[#cbd5e1] overflow-auto">
                    {JSON.stringify(
                      {
                        contract_version: plan.contract_version,
                        flow_id: plan.flow.id,
                        input: plan.input,
                        missing: plan.missing,
                        optional_pending: plan.optional_pending,
                      },
                      null,
                      2
                    )}
                  </pre>
                ) : (
                  <div className="w-full max-w-2xl space-y-3">
                    <div className="rounded-2xl border border-white/10 bg-[#11151e]/95 p-5 space-y-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#2DD4BF]/15 border border-[#2DD4BF]/30 text-[#2DD4BF] text-[10px] font-mono font-semibold uppercase tracking-wide">
                          <Clapperboard className="w-3 h-3" />
                          {plan.flow.label}
                        </span>
                        {plan.flow_pending && !executed ? (
                          <span className="text-[10px] font-mono text-amber-300">
                            {plan.story_format_pending
                              ? 'Story format not chosen yet'
                              : plan.format_unclear
                                ? 'Format not chosen yet'
                                : 'Needs your confirmation'}
                          </span>
                        ) : plan.confidence != null ? (
                          <span className="text-[10px] font-mono text-[#9CA3AF]">
                            {Math.round(Number(plan.confidence) * 100)}% match
                          </span>
                        ) : null}
                        <span className="text-[10px] text-[#6B7280]">
                          {plan.flow.execution === 'api' ? 'Runs automatically' : 'Opens flow prefilled'}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setEditingField('title')}
                        disabled={busy}
                        title="Edit title"
                        className="text-left w-full cursor-pointer group"
                      >
                        <h3 className="text-xl font-bold text-white leading-tight group-hover:text-[#5EEAD4]">
                          {plan.input.title || plan.suggestions?.title || 'Untitled'}
                        </h3>
                      </button>
                      {plan.input.brief ? (
                        <p className="text-sm text-[#cbd5e1] line-clamp-4">{plan.input.brief}</p>
                      ) : null}
                      {plan.alternates?.length && !executed ? (
                        <div className="flex items-center gap-1.5 flex-wrap pt-1">
                          <span className="text-[10px] text-[#6B7280]">Not right?</span>
                          {plan.alternates.map((alt) => (
                            <button
                              key={alt.id}
                              type="button"
                              disabled={busy}
                              onClick={() => runAnswer({ field: 'flow_id', value: alt.id }, `Make it ${alt.label} instead`)}
                              className="px-2 py-0.5 rounded-full text-[10px] border border-white/15 text-[#E5E7EB] hover:border-[#2DD4BF]/50 cursor-pointer disabled:opacity-50"
                            >
                              {alt.label}
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </div>

                    {castPlan ? (
                      <CastStatusPanel
                        plan={plan}
                        executed={executed}
                        busyStep={castBusy}
                        onOpenBuilder={(focusId) => setCastDialog({ kind: 'builder', focusId: focusId || null })}
                        onOpenVoices={() => setCastDialog({ kind: 'voices' })}
                        onRegenerateOverview={regenerateCastOverview}
                        onSaveOverview={saveCastOverview}
                      />
                    ) : null}

                    {coursePlanFlow ? (
                      <CourseStatusPanel
                        course={course}
                        busyStep={courseBusy}
                        onOpenNarration={() =>
                          startLessonArtifacts({
                            language: course?.language || plan?.input?.language || 'en',
                            tone: course?.tone || 'serious',
                          })
                        }
                        onOpenAlignPopup={() => {
                          if (course?.align_status === 'completed' || course?.align_result) {
                            setAlignPopupOpen(true);
                          } else {
                            startCourseAlignment();
                          }
                        }}
                        onOpenEditor={async () => {
                          if (!course?.align_job_id) return;
                          try {
                            const data = await openScriptAudioAlignInEditor(course.align_job_id);
                            const path = data?.editor_path || data?.path;
                            if (path && navigate) {
                              onClose?.();
                              navigate(path);
                            } else {
                              toast.success('Opened in editor');
                            }
                          } catch (err) {
                            toast.error(formatApiError(err, 'Could not open editor'));
                          }
                        }}
                      />
                    ) : null}

                    {reelsPlan && reelsProjectId ? (
                      <ReelsShortsAgentPanel
                        projectId={reelsProjectId}
                        job={job}
                        busyStep={reelsBusy}
                        refreshKey={reelsRefreshKey}
                        onOpenTranscripts={() => setReelsDialog({ kind: 'transcripts' })}
                        onOpenPlacement={() => setReelsDialog({ kind: 'placement' })}
                        onCleanAudio={() => void runReelsAudioPolish({ auto: false })}
                        onRetranscribe={() => void runReelsRetranscribe()}
                        onRetrySplitAv={() => void runReelsRetrySplitAv()}
                        onRunEditor={runReelsAiEditor}
                        onRecut={() => void runReelsRecut()}
                        onOpenEditor={openReelsEditor}
                      />
                    ) : null}

                    <PlanFieldTiles
                      paths={visibleFields}
                      plan={plan}
                      missing={missing}
                      optionalPending={optionalPending}
                      busy={busy}
                      onEdit={setEditingField}
                    />

                    {detectedStyleNote ? (
                      <button
                        type="button"
                        onClick={() => setEditingField('visual.style')}
                        className="w-full rounded-2xl border border-[#2DD4BF]/25 bg-[#2DD4BF]/5 p-4 text-left cursor-pointer hover:border-[#2DD4BF]/50"
                      >
                        <p className="text-[10px] uppercase tracking-wide text-[#2DD4BF] mb-1.5 flex items-center gap-1.5">
                          <Sparkles className="w-3 h-3" /> Applied visual style
                          <PenLine className="w-3 h-3 ml-auto opacity-70" />
                        </p>
                        <p className="text-[12px] text-[#E5E7EB] leading-relaxed whitespace-pre-wrap">
                          {detectedStyleNote}
                        </p>
                      </button>
                    ) : null}

                    {plan.steps?.length ? (
                      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
                        <p className="text-[10px] uppercase tracking-wide text-[#6B7280] mb-2">Production steps</p>
                        <ol className="space-y-1.5">
                          {plan.steps.map((step, idx) => (
                            <li key={`${step.title}-${idx}`} className="text-xs text-[#E5E7EB] flex gap-2">
                              <span className="font-mono text-[#2DD4BF]">{idx + 1}.</span>
                              <span>
                                {step.title}
                                {step.detail ? <span className="text-[#9CA3AF]"> — {step.detail}</span> : null}
                              </span>
                            </li>
                          ))}
                        </ol>
                      </div>
                    ) : null}

                    {plan.assumptions?.length ? (
                      <ul className="text-[11px] text-[#9CA3AF] space-y-1 list-disc pl-5">
                        {plan.assumptions.map((a) => (
                          <li key={a}>{a}</li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                )}
              </div>
            </div>
          </div>
          {optionsPopupOpen && question?.options?.length ? (
            <div
              className="absolute inset-0 z-10 flex items-center justify-center bg-black/70 p-4"
              onClick={() => setOptionsPopupOpen(false)}
              role="presentation"
            >
              <div
                role="dialog"
                aria-modal="true"
                aria-label={question.prompt}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setOptionsPopupOpen(false);
                }}
                data-testid="create-plan-options-popup"
                className="w-full max-w-3xl max-h-[85vh] flex flex-col rounded-2xl border border-white/10 bg-[#0b0e14] shadow-2xl"
              >
                <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-3 border-b border-white/10">
                  <div>
                    <p className="text-sm font-semibold text-white">{question.prompt}</p>
                    <p className="text-[11px] text-[#9CA3AF]">
                      {multiChoice ? 'Pick as many as you like.' : 'Pick one.'} {question.options.length} options
                    </p>
                  </div>
                  <button
                    type="button"
                    autoFocus
                    onClick={() => setOptionsPopupOpen(false)}
                    aria-label="Close"
                    className="p-1.5 rounded-lg text-[#9CA3AF] hover:text-white hover:bg-white/10 cursor-pointer"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div
                  className={`overflow-y-auto p-5 ${
                    imageOptions
                      ? 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3'
                      : 'flex flex-wrap gap-2 content-start'
                  }`}
                >
                  {question.options.map(imageOptions ? renderOptionCard : renderOptionChip)}
                </div>
                {multiChoice ? (
                  <div className="flex justify-end gap-2 px-5 py-3 border-t border-white/10">
                    <button
                      type="button"
                      onClick={() => setOptionsPopupOpen(false)}
                      disabled={!multiAnswerCount}
                      className="px-3.5 py-1.5 rounded-full text-xs font-semibold cursor-pointer bg-[#2DD4BF] text-[#06080D] flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Check className="w-3.5 h-3.5" /> Done{multiAnswerCount ? ` · ${multiAnswerCount} selected` : ''}
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}

                {/* Image lightbox */}
          {lightboxUrl ? (
            <div
              className="absolute inset-0 z-20 flex items-center justify-center bg-black/85 p-4 cursor-zoom-out"
              onClick={() => setLightboxUrl(null)}
            >
              <div className="relative max-w-4xl max-h-[90vh] w-full flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                <img src={lightboxUrl} alt="Reference" className="max-w-full max-h-[85vh] rounded-2xl shadow-2xl object-contain" />
                <button type="button" onClick={() => setLightboxUrl(null)}
                  className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-[#0b0e14] border border-white/20 text-white flex items-center justify-center hover:bg-white/10 cursor-pointer" aria-label="Close">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          ) : null}

          {/* Style reference image upload popup */}
          {stylePopupOpen ? (
            <div
              className="absolute inset-0 z-10 flex items-end sm:items-center justify-center bg-black/70 p-4"
              onClick={() => setStylePopupOpen(false)}
              role="presentation"
            >
              <div
                role="dialog"
                aria-modal="true"
                aria-label="Upload reference images for visual style"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => { if (e.key === 'Escape') setStylePopupOpen(false); }}
                className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#0b0e14] shadow-2xl flex flex-col"
              >
                {/* Header */}
                <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-white/10">
                  <div>
                    <p className="text-sm font-semibold text-white flex items-center gap-2">
                      <Image className="w-4 h-4 text-[#93C5FD]" /> Reference images — visual style
                    </p>
                    <p className="text-[11px] text-[#9CA3AF] mt-0.5">Upload images to detect and describe the visual style.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setStylePopupOpen(false)}
                    className="p-1.5 rounded-lg text-[#9CA3AF] hover:text-white hover:bg-white/10 cursor-pointer"
                    aria-label="Close"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {/* Upload area — uses label+input for reliable cross-browser file pick */}
                <div className="px-5 pt-4 pb-2">
                  <input
                    id="style-ref-img-input"
                    type="file"
                    accept={ATTACH_ACCEPT.image}
                    multiple
                    className="sr-only"
                    disabled={styleUploading}
                    onChange={(e) => {
                      handleStyleImageFiles(e.target.files);
                      e.target.value = '';
                    }}
                  />
                  <label
                    htmlFor="style-ref-img-input"
                    className={`w-full rounded-xl border border-dashed border-[#93C5FD]/40 bg-[#93C5FD]/5 py-5 text-xs text-[#93C5FD]/80 hover:border-[#93C5FD]/70 hover:text-[#93C5FD] flex flex-col items-center gap-2 transition-colors ${styleUploading ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    {styleUploading ? (
                      <Loader2 className="w-6 h-6 animate-spin" />
                    ) : (
                      <Upload className="w-6 h-6" />
                    )}
                    <span>{styleUploading ? 'Analyzing style…' : 'Click to upload images (up to 4)'}</span>
                    <span className="text-[10px] text-[#6B7280]">PNG, JPG, WEBP, GIF</span>
                  </label>

                  {/* Thumbnails */}
                  {styleImages.length > 0 ? (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {styleImages.map((img) => (
                        <div key={img.id} className="relative group">
                          <img src={img.previewUrl} alt="style ref" className="w-16 h-16 rounded-lg object-cover border border-white/10" />
                          {(img.status === 'uploading' || img.status === 'analyzing') ? (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/60 rounded-lg">
                              <Loader2 className="w-4 h-4 animate-spin text-[#7DD3FC]" />
                            </div>
                          ) : null}
                          <button
                            type="button"
                            onClick={() => removeStyleImage(img.id)}
                            className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#0b0e14] border border-white/20 text-[#9CA3AF] hover:text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-2.5 h-2.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>

                {/* Editable detected style text */}
                <div className="px-5 pb-2">
                  <label className="text-[11px] text-[#9CA3AF] mb-1.5 flex items-center gap-1">
                    <Sparkles className="w-3 h-3 text-[#2DD4BF]" />
                    Detected style description
                    <span className="text-[10px] opacity-60">(edit before using)</span>
                  </label>
                  <textarea
                    rows={4}
                    value={styleNoteEditing !== '' ? styleNoteEditing : detectedStyleNote}
                    onChange={(e) => setStyleNoteEditing(e.target.value)}
                    placeholder={styleUploading ? 'Analyzing images…' : 'Style description will appear here after upload, or type your own…'}
                    className="w-full bg-white/[0.04] border border-white/10 focus:border-[#2DD4BF]/60 rounded-xl px-3 py-2 text-xs text-white placeholder:text-[#4B5563] focus:outline-none resize-none"
                  />
                </div>

                {/* Footer actions */}
                <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-white/10">
                  <button
                    type="button"
                    onClick={() => setStylePopupOpen(false)}
                    className="px-3 py-1.5 text-xs text-[#9CA3AF] hover:text-white cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={!(styleNoteEditing || detectedStyleNote).trim()}
                    onClick={() => {
                      const note = (styleNoteEditing || detectedStyleNote).trim();
                      if (!note) return;
                      setDetectedStyleNote(note);
                      setStyleNoteEditing('');
                      setStylePopupOpen(false);
                      answerQuestion(note, 'Style from reference images');
                    }}
                    className="px-4 py-1.5 rounded-full text-xs font-semibold bg-[#2DD4BF] text-[#06080D] flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                  >
                    <Sparkles className="w-3.5 h-3.5" /> Use detected style
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          {/* Dialogs must live inside the studio stacking context (isolation:isolate) or clicks look dead. */}
          {coursePlanFlow && !executed && courseDialog?.kind === 'narration' ? (
            <CourseNarrationDialog
              open
              language={course?.language || plan?.input?.language || 'en'}
              tone={course?.tone || 'serious'}
              depth={course?.depth}
              onClose={() => setCourseDialog(null)}
              onConfirm={confirmCourseNarration}
              busy={courseBusy === 'explain'}
            />
          ) : null}
          {coursePlanFlow ? (
            <CourseAlignPipelinePopup
              open={alignPopupOpen}
              onClose={() => setAlignPopupOpen(false)}
              result={course?.align_result}
              loading={courseBusy === 'align'}
              error={course?.align_error || null}
            />
          ) : null}
          {unifiedStory ? (
            <UnifiedStoryPipelineDialog
              open={storyPipelineOpen}
              onClose={() => setStoryPipelineOpen(false)}
              plan={plan}
            />
          ) : null}
          {plan && castDialog?.kind === 'builder' ? (
            <CastCharacterBuilderDialog
              key={`builder-${plan?.id}`}
              plan={plan}
              focusId={castDialog.focusId}
              executed={executed}
              restarting={retrying}
              onPlan={setPlan}
              onClose={() => setCastDialog(null)}
              onDone={onCastCharactersDone}
              onRestartStory={() => handleRetry({ withLooks: true })}
            />
          ) : null}
          {castPlan && !executed && castDialog?.kind === 'voices' ? (
            <CastVoiceDialog
              key={`voices-${plan?.id}`}
              plan={plan}
              onPlan={setPlan}
              onClose={() => setCastDialog(null)}
              onDone={onCastVoicesDone}
            />
          ) : null}
          {editingField && plan ? (
            <PlanFieldEditorDialog
              plan={plan}
              path={editingField}
              locked={executed}
              busy={busy}
              onSave={savePlanField}
              onClose={() => setEditingField(null)}
              onRegenerateScript={
                editingField === 'script' ? (prompt) => submitScriptGeneration(prompt) : undefined
              }
            />
          ) : null}
          {reelsPlan && reelsDialog?.kind === 'transcripts' && reelsProjectId ? (
            <ReelsShortsTranscriptDialog
              open
              projectId={reelsProjectId}
              onClose={() => setReelsDialog(null)}
              onSaved={() => {
                setReelsRefreshKey((k) => k + 1);
                pushAssistant(
                  'Subtitles saved. Choose title / subtitle placement for the first N seconds, then Generate edit.'
                );
                setReelsDialog({ kind: 'placement' });
              }}
            />
          ) : null}
          {reelsPlan && reelsDialog?.kind === 'placement' && reelsProjectId ? (
            <ReelsShortsPlacementDialog
              open
              projectId={reelsProjectId}
              onClose={() => setReelsDialog(null)}
              onSaved={(overlay) => {
                setReelsRefreshKey((k) => k + 1);
                const secs = overlay?.duration_seconds > 0 ? overlay.duration_seconds : 'full';
                pushAssistant(
                  `Placement saved — title ${overlay?.placement || 'top'} for ${secs === 'full' ? 'the full clip' : `the first ${secs}s`}, captions ${overlay?.caption_placement || 'bottom'}. Hit **Generate edit** when ready.`
                );
              }}
            />
          ) : null}
        </motion.div>
      ) : null}
    </AnimatePresence>
  );

  const publishTitle = outputs?.title || plan?.input?.title || '';
  const publishProps = {
    oauthReturnPath: `${window.location.pathname}${window.location.search}`,
    defaultVideoUrl: finalVideoUrl,
    defaultTitle: publishTitle,
    scriptContext: plan?.input?.brief || publishTitle,
    tone: plan?.input?.tone || '',
    visualStyle: plan?.input?.visual?.style || '',
    narrationProjectId: outputs?.narration_project_id || null,
    storyId: outputs?.story_id || null,
    defaultShortsBoost:
      String(outputs?.aspect_ratio || plan?.input?.aspect_ratio || '').startsWith('9:'),
    storyLanguage:
      plan?.input?.language
      || plan?.script_language?.preferred
      || plan?.script_language?.language
      || '',
  };

  return (
    <>
      {createPortal(studio, document.body)}
      {open ? (
        <>
          <YouTubePublishDialog
            open={publishDialog === 'youtube'}
            onOpenChange={closePublishDialog}
            {...publishProps}
            {...publishCallbacks('youtube')}
          />
          <InstagramPublishDialog
            open={publishDialog === 'instagram'}
            onOpenChange={closePublishDialog}
            {...publishProps}
            {...publishCallbacks('instagram')}
          />
        </>
      ) : null}
    </>
  );
}

export default CreatePlanChatStudio;
